/**
 * JobsView (JOB-LIFECYCLE slice 1) — the install jobs screen. List of Pegasus
 * jobs + "Sell an install" (create job + install visit) + a Job detail slide-out
 * showing the CONNECTED STORY (sold info, originating call, visits, pipeline
 * stage). Close-out (equipment/permit/invoice) lands in the next slice.
 */
import { useState, useEffect, useMemo, useRef, Fragment, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Hammer, Plus, Check, X, Trash2, MapPin, CalendarClock, ArrowRight, ArrowLeft, User, Pencil,
  Package, FileText, AlertCircle, CheckCircle2, Lock, Clock, Calculator, FilePlus2, Search, Home,
  Droplets, Receipt, Eye, EyeOff, Printer, ExternalLink, ChevronUp, ChevronDown, HandCoins, ClipboardCheck, Banknote, FlaskConical, type LucideIcon,
} from "lucide-react";
// PAY-02 entry point: collect a payment on the finalized install invoice from the workspace.
import { CollectPaymentModal } from "@/features/payments/CollectPaymentModal";
import { useCustomerPayments, invoicePaidMap, docPaymentLines } from "@/features/payments/payments";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { cn } from "@/lib/cn";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { PageHeader } from "@/components/ui/PageHeader";
import { Tabs } from "@/components/ui/Tabs";
import { TeamMemberSelect, useMemberName } from "@/components/TeamMemberSelect";
import { NotesEditor } from "@/components/NotesEditor";
import { useCustomerLookup, type CustomerLite, useNativePos, useEscPoList, useNativePoLines, useEscPoLines, useJobMatchingPos } from "@/features/purchase-orders/po";
import {
  type Job, type JobEquipment, type JobVisit, type JobQuote,
  useJobs, useJob, useJobVisits, useAssignJobVisit, useCreateInstallVisit, useSellInstall, useCreateNCJob, useUpdateJob, useDeleteJob,
  useToggleJobCheck, useJobBenchmarks, useAddJobBenchmark, useToggleJobBenchmark, useDeleteJobBenchmark,
  useInvoiceLineEdits, useSetInvoiceLineHidden, useAddCustomInvoiceLine, useDeleteInvoiceLineEdit, useUpdateCustomInvoiceLine, useReorderInvoiceLines,
  useJobSalesCredits, useAddSalesCredit, useDeleteSalesCredit, manufacturerLabel,
  useCustomerDispatches, useJobEquipment, useAddJobEquipment, useUpdateJobEquipment,
  useConfirmInstallEquipment, useDeleteJobEquipment, useJobPermits, effectivePermitBy, permitIsOnFile, type PermitBy,
  useCloseJob, useJobInvoice, useJobInvoices, useJobParts,
  useJobLabor, useAddJobLabor, useDeleteJobLabor,
  useChangeOrders, useAddChangeOrder, useUpdateChangeOrderStatus, useDeleteChangeOrder, useJobQuote, useJobQueues,
  useJobMaterials, useAddJobMaterial, useUpdateJobMaterial, useDeleteJobMaterial, materialCost,
  JOB_STAGES, STAGE_LABEL,
} from "./jobs";
import { SerialField } from "./SerialField";
import { InternalNotes } from "@/lib/internalNotes";
import { useJobProperty, useNewHomeownerHandoff } from "./properties";
import { SubcontractorSendCard, JobSummaryTab, WarehouseTab, firstInstallDay, useJobSends, useSubcontractors } from "./WorkOrderTab";
import { JobDocumentsCard } from "./JobDocumentsCard";
import { subSendState } from "./resendPrompt";
import { InstallDetailsCard } from "./InstallDetailsCard";
import { specProgress, useJobEquipFacts, useInstallBank } from "./installSpec";
import { SubInvoiceReviewRail, JobSubInvoices } from "./SubInvoiceReview";
import { SentComms } from "./SentComms";
import { PostInstallCard } from "@/features/dispatch-detail/PostInstallCard";
import { usePostInstall } from "@/features/dispatch-detail/postInstall";
import { PermitSection, permitLabel } from "./PermitSection";
import { NCBillingSection, NCCloseOutSection, BuilderSettingsModal } from "./NCBillingPanel";
import { useBuilder, useNcDrawDefault, effectiveDrawPlan } from "./ncBilling";
import { useRemovedEquipment, DISPOSITION_LABEL, useJobMoney } from "./installField";
import { useJobPayments, COUNTED } from "./fieldPay";
import { InfoTip } from "@/components/InfoTip";
import { useRealtimeRefresh } from "@/lib/useRealtimeRefresh";
import { useJobFiles, useSignedUrls } from "@/features/files/filesCore";
import { useAllActiveTechs, useTechRates } from "@/features/board/api";
import { useCreateQuote } from "@/features/quotes/quotes";
import { SalePaymentLine } from "@/features/quotes/SalePaymentLine";
import { JobPayPanel } from "./JobPayPanel";
import { InstallRecordSection, InstallRecordCloseChecks } from "./InstallRecord";
import { lineStatusText, useJobMaterialRequests } from "./materialRequests";
import { AhriCard } from "./AhriCard";
import { SoldStage } from "./SoldStage";
import { effectiveLoad, soldState, usePersonName, useLoadCalcRules } from "./saleHandoff";
import { PricebookPicker } from "@/features/items/PricebookPicker";
import { itemCostOf } from "@/features/items/pricebookSearch";
import { useJobRefrigerant, useRefrigerantItems, useRefrigerantTanks, refrigerantCost, ozText } from "./refrigerant";

const money = (n: number | null | undefined) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtDate = (s: string | null | undefined) =>
  !s ? "" : new Date(s.length <= 10 ? s + "T00:00:00" : s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

/** Shared clean input style for the job workspace (replaces the old `.jinp` hack). */
const fieldCls = "w-full rounded-md border border-hairline bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30";
/** Uniform section-card header (icon + title). `right` takes a live status element. */
/** A card title. `note` is a LIVE fact on the right ("3 hidden", "12h total");
 *  `info` is the how / why, behind the (i) (DESC-CLEAN-01 — no grey explainer lines). */
function SectionHead({ icon: Icon, title, note, info, right }: { icon: LucideIcon; title: string; note?: string; info?: ReactNode; right?: ReactNode }) {
  return (
    <h3 className="mb-3 flex flex-wrap items-center gap-2 text-base font-semibold text-slate-800">
      <Icon className="h-[18px] w-[18px] text-brand-500" /> {title}
      {info && <InfoTip label={`About ${title}`}>{info}</InfoTip>}
      {note && <span className="ml-auto text-sm font-medium text-slate-500">{note}</span>}
      {right && <span className="ml-auto">{right}</span>}
    </h3>
  );
}

/** The visit chip in plain words (was the raw "pending_schedule"). */
const VISIT_STATUS_LABEL: Record<string, string> = {
  pending_schedule: "Needs a Date", scheduled: "Scheduled", on_hold: "On Hold", completed: "Completed", cancelled: "Cancelled",
};
const titleWords = (s: string | null | undefined) => (s ?? "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function JobsView() {
  const { data: jobs, isLoading } = useJobs();
  const navigate = useNavigate();
  const [selling, setSelling] = useState(false);
  const [creatingNC, setCreatingNC] = useState(false);
  const [builderFor, setBuilderFor] = useState<{ custno: string; name: string } | null>(null);
  const [tab, setTab] = useState<"install" | "nc">("install");
  const [q, setQ] = useState("");
  // WORK-QUEUES-01 — "who needs to act" filter chips over the derived queues.
  // Deep-linkable: Today's queue chips land here with ?queue=needsPermit.
  const [queue, setQueue] = useState<"all" | "needsSchedule" | "needsPermit" | "readyToClose">(() => {
    const q0 = new URLSearchParams(window.location.search).get("queue");
    return q0 === "needsSchedule" || q0 === "needsPermit" || q0 === "readyToClose" ? q0 : "all";
  });
  const [range, setRange] = useState<RangeKey>("all");
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const all = jobs ?? [];
  const { data: queues } = useJobQueues(jobs);

  const matchQ = (j: Job) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return `${j.title ?? ""} ${j.customer_name ?? ""} ${j.address ?? ""}`.toLowerCase().includes(s);
  };
  const list = useMemo(() => {
    const [s, e] = rangeBounds(range, from, to);
    return all
      .filter((j) => (tab === "nc" ? isNC(j) : !isNC(j)))
      .filter(matchQ)
      .filter((j) => (queue === "all" ? true : (queues?.[queue]?.has(j.id) ?? false)))
      .filter((j) => {
        if (!s && !e) return true;
        const d = new Date(j.created_at);
        return (!s || d >= s) && (!e || d <= e);
      });
  }, [all, tab, q, range, from, to, queue, queues]);
  const counts = useMemo(
    () => ({ install: all.filter((j) => !isNC(j)).length, nc: all.filter((j) => isNC(j)).length }),
    [all],
  );
  const tabIds = useMemo(() => new Set(all.filter((j) => (tab === "nc" ? isNC(j) : !isNC(j))).map((j) => j.id)), [all, tab]);
  const permitAsk = (id: string) => queues?.permitRequested.has(id)
    ? <span className="ml-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10.5px] font-bold text-amber-800" title="The crew asked for a permit — nobody has pulled it yet">Permit requested</span>
    : null;

  // New-construction homes group under their builder with a per-builder roll-up.
  const groups = useMemo(() => {
    const m = new Map<string, Job[]>();
    for (const j of list) { if (!m.has(j.custno)) m.set(j.custno, []); m.get(j.custno)!.push(j); }
    return [...m.entries()].map(([custno, js]) => ({
      custno, name: js[0].customer_name ?? custno, jobs: js,
      contract: js.reduce((s, j) => s + (j.quoted ?? 0), 0),
      cost: js.reduce((s, j) => s + (j.actual ?? 0), 0),
      margin: js.reduce((s, j) => s + (j.variance ?? 0), 0),
    })).sort((a, b) => b.jobs.length - a.jobs.length || a.name.localeCompare(b.name));
  }, [list]);

  const memberName = useMemberName();
  return (
    <div className="mx-auto max-w-7xl p-6">
      <PageHeader
        icon={Hammer}
        title="Jobs"
        description={tab === "nc"
          ? "New-construction homes — grouped by builder, with live job-costing margin."
          : "Service installs sold from the field or office — one job, one close-out invoice."}
        actions={tab === "nc"
          ? <Button onClick={() => setCreatingNC(true)}><Plus className="h-4 w-4" /> New construction job</Button>
          : <Button onClick={() => setSelling(true)}><Plus className="h-4 w-4" /> Sell an install</Button>}
      />

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        {/* SPREAD-01: the hand-rolled gray toggle retires — same shared
            segmented control the 360 and Invoices use (gliding pill, counts). */}
        <Tabs
          variant="pill"
          size="sm"
          value={tab}
          onChange={(k) => setTab(k as "install" | "nc")}
          items={[
            { key: "install", label: "Service Installs", count: counts.install || null },
            { key: "nc", label: "New Construction", count: counts.nc || null },
          ]}
        />
        <div className="flex flex-wrap items-center gap-2">
          <DateRangeControl range={range} setRange={setRange} from={from} setFrom={setFrom} to={to} setTo={setTo} />
          <div className="relative w-64 max-w-full">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={tab === "nc" ? "Search builder, home address…" : "Search customer, address, job…"}
              className="w-full rounded-md border border-hairline py-1.5 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
          </div>
        </div>
      </div>

      {/* POWERHOUSE-INGEST — invoices read from the electrician's emails,
          waiting for a human Apply. Renders nothing when the queue is empty. */}
      {tab === "install" && <SubInvoiceReviewRail />}

      {/* WORK-QUEUES (Seth 2026-08-18: "everyone knows what they are supposed
          to do and what is pending"): derived action queues — a job enters by
          its state, leaves when the state changes. Nothing assigned, nothing
          remembered. Post-install joins when its stations ship. */}
      {/* PERMIT-01: the queues show on both tabs, counted for the tab you're on
          (a new-construction house needs its permit desk too). */}
      {(
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          {([
            ["all", "All open", null],
            ["needsSchedule", "Needs scheduling", "install coordinator"],
            ["needsPermit", "Needs permit", "permit desk"],
            ["readyToClose", "Ready to close", "install coordinator"],
          ] as const).map(([k, label, who]) => {
            const n = k === "all" ? null : [...(queues?.[k] ?? [])].filter((id) => tabIds.has(id)).length;
            const nReq = k === "needsPermit" ? [...(queues?.permitRequested ?? [])].filter((id) => tabIds.has(id)).length : 0;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setQueue(k)}
                title={who ? `Usually the ${who}'s queue` : undefined}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold transition-colors",
                  queue === k ? "bg-brand-700 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
                )}
              >
                {label}
                {n != null && (
                  <span className={cn(
                    "rounded-full px-1.5 text-xs tabular-nums",
                    queue === k ? "bg-white/20" : n > 0 ? "bg-amber-100 text-amber-800" : "bg-white text-slate-400",
                  )}>
                    {n}
                  </span>
                )}
                {nReq > 0 && (
                  <span title="The crew or office asked for these and nobody has pulled them yet"
                    className={cn("rounded-full px-1.5 text-[11px] font-bold", queue === k ? "bg-white/25" : "bg-amber-500 text-white")}>
                    {nReq} requested
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-hairline bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-hairline bg-slate-50/70 text-left text-[12px] font-semibold text-slate-500">
              {tab === "nc" ? (
                <>
                  <th className="px-3 py-2">Home / job</th><th className="px-3 py-2">Supervisor</th>
                  <th className="px-3 py-2">Stage</th><th className="px-3 py-2 text-right">Contract</th>
                  <th className="px-3 py-2 text-right">Cost</th><th className="px-3 py-2 text-right">Margin</th>
                </>
              ) : (
                <>
                  <th className="px-3 py-2">Job</th><th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2">Sold by</th><th className="px-3 py-2">Stage</th>
                  <th className="px-3 py-2 text-right">Price</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>}
            {!isLoading && list.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-slate-400">
                {q.trim()
                  ? <>No {tab === "nc" ? "homes" : "installs"} match “<span className="font-medium text-slate-600">{q}</span>”.</>
                  : tab === "nc"
                    ? "No new-construction jobs yet."
                    : <>No installs yet. Click <span className="font-medium text-slate-600">Sell an install</span> to create one.</>}
              </td></tr>
            )}

            {tab === "nc" && groups.map((g) => (
              <Fragment key={g.custno}>
                <tr className="border-b border-hairline bg-slate-50/70">
                  <td className="px-3 py-1.5 font-semibold text-slate-700">{g.name}<span className="ml-1 font-normal text-slate-400"> · {g.jobs.length} {g.jobs.length === 1 ? "home" : "homes"}</span>
                    <button type="button" onClick={() => setBuilderFor({ custno: g.custno, name: g.name })} title="Draw split, permits, PO, billing email for this builder"
                      className="ml-2 text-[11px] font-semibold text-brand-700 hover:underline">Builder Settings</button></td>
                  <td /><td />
                  <td className="px-3 py-1.5 text-right text-xs tabular-nums text-slate-500">{money(g.contract)}</td>
                  <td className="px-3 py-1.5 text-right text-xs tabular-nums text-slate-500">{money(g.cost)}</td>
                  <td className={cn("px-3 py-1.5 text-right text-xs font-bold tabular-nums", g.margin >= 0 ? "text-emerald-700" : "text-rose-600")}>{money(g.margin)}</td>
                </tr>
                {g.jobs.map((j) => (
                  <tr key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} className="cursor-pointer border-b border-hairline last:border-0 hover:bg-slate-50">
                    <td className="px-3 py-2 pl-6"><span className="font-medium text-slate-800">{j.title ?? "Home"}</span>{j.is_test && <TestTag />}{permitAsk(j.id)}{j.address ? <span className="ml-1 text-xs text-slate-400">{j.address}</span> : null}{j.custno && <Link to={`/customers/${j.custno}`} onClick={(e) => e.stopPropagation()} className="ml-2 text-[11px] font-semibold text-brand-700 hover:underline">360↗</Link>}</td>
                    <td className="px-3 py-2 text-slate-500">{memberName(j.supervisor ?? j.sale_tech) ?? "—"}</td>
                    <td className="px-3 py-2"><StageChip status={j.status} /></td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">{money(j.quoted)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-500">{money(j.actual)}</td>
                    <td className={cn("px-3 py-2 text-right font-medium tabular-nums", (j.variance ?? 0) >= 0 ? "text-emerald-700" : "text-rose-600")}>{money(j.variance)}</td>
                  </tr>
                ))}
              </Fragment>
            ))}

            {tab === "install" && list.map((j) => (
              <tr key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} className="cursor-pointer border-b border-hairline last:border-0 hover:bg-slate-50">
                <td className="px-3 py-2"><span className="font-medium text-slate-800">{j.title ?? "Install"}</span>{j.is_test && <TestTag />}{permitAsk(j.id)}</td>
                <td className="px-3 py-2 text-slate-600">{j.customer_name ?? "—"}{j.address ? <span className="ml-1 text-xs text-slate-400">{j.address}</span> : null}{j.custno && <Link to={`/customers/${j.custno}`} onClick={(e) => e.stopPropagation()} className="ml-2 text-[11px] font-semibold text-brand-700 hover:underline">360↗</Link>}</td>
                <td className="px-3 py-2 text-slate-500">{memberName(j.sale_tech) ?? "—"}</td>
                <td className="px-3 py-2"><StageChip status={j.status} /></td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-600">{money(j.sell_price ?? j.quoted)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Creating an install goes STRAIGHT to the job workspace — no detour
          through the detail slide-over (Seth 2026-08-18: "opens up a weird
          side window before taking me into the actual job"). */}
      {selling && <SellInstallModal onClose={() => setSelling(false)} onCreated={(id) => { setSelling(false); navigate(`/jobs/${id}`); }} />}
      {creatingNC && <NewConstructionModal onClose={() => setCreatingNC(false)} onCreated={(id) => { setCreatingNC(false); navigate(`/jobs/${id}`); }} />}
      {builderFor && <BuilderSettingsModal custno={builderFor.custno} name={builderFor.name} onClose={() => setBuilderFor(null)} />}
    </div>
  );
}

/** New-construction jobs are flagged by job_type; everything else is a residential service install. */
function isNC(j: Job) { return j.job_type === "new_construction" || j.job_type === "nc"; }

/** TEST-FLAG-01: the small "Test" tag on a test job's row. */
function TestTag() {
  return (
    <span title="Test job — never messages anyone" className="ml-2 inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 align-[1px] text-[12px] font-bold text-rose-700 ring-1 ring-inset ring-rose-200">
      <FlaskConical className="h-3 w-3" /> Test
    </span>
  );
}

// ── Date-range filter ────────────────────────────────────────────────────────
type RangeKey = "all" | "30d" | "month" | "quarter" | "year" | "custom";
const RANGE_LABEL: Record<RangeKey, string> = {
  all: "All time", "30d": "Last 30 days", month: "This month", quarter: "This quarter", year: "This year", custom: "Custom…",
};
/** Resolve a range key (over a job's created/sold date) to [start, end] bounds. */
function rangeBounds(key: RangeKey, from: string, to: string): [Date | null, Date | null] {
  const now = new Date();
  switch (key) {
    case "all": return [null, null];
    case "30d": { const s = new Date(now); s.setDate(s.getDate() - 30); return [s, null]; }
    case "month": return [new Date(now.getFullYear(), now.getMonth(), 1), null];
    case "quarter": return [new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1), null];
    case "year": return [new Date(now.getFullYear(), 0, 1), null];
    case "custom": return [from ? new Date(from) : null, to ? new Date(to + "T23:59:59") : null];
  }
}

function DateRangeControl({ range, setRange, from, setFrom, to, setTo }: {
  range: RangeKey; setRange: (r: RangeKey) => void;
  from: string; setFrom: (s: string) => void; to: string; setTo: (s: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <select value={range} onChange={(e) => setRange(e.target.value as RangeKey)}
        className="rounded-md border border-hairline py-1.5 pl-2.5 pr-7 text-sm text-slate-600 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30">
        {(Object.keys(RANGE_LABEL) as RangeKey[]).map((k) => <option key={k} value={k}>{RANGE_LABEL[k]}</option>)}
      </select>
      {range === "custom" && (
        <>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From date"
            className="rounded-md border border-hairline px-2 py-1.5 text-sm text-slate-600 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
          <span className="text-xs text-slate-400">to</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To date"
            className="rounded-md border border-hairline px-2 py-1.5 text-sm text-slate-600 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
        </>
      )}
    </div>
  );
}

function StageChip({ status }: { status: string }) {
  const tone = status === "closed" ? "bg-emerald-100 text-emerald-700"
    : status === "cancelled" ? "bg-rose-100 text-rose-700"
    : status === "sold" ? "bg-violet-100 text-violet-700"
    : "bg-brand-100 text-brand-700";
  return <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", tone)}>{STAGE_LABEL[status] ?? status}</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
export function SellInstallModal({ prefill, onClose, onCreated }: {
  prefill?: { customer: CustomerLite; sourceDispatch?: string | null } | null;
  onClose: () => void; onCreated: (jobId: string) => void;
}) {
  const sell = useSellInstall();
  const navigate = useNavigate();
  const createQuote = useCreateQuote();
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<CustomerLite | null>(prefill?.customer ?? null);
  const { data: matches } = useCustomerLookup(term);
  // BUILDER-FIRST (Seth 2026-08-18: "maybe I don't know the price until I
  // start to build everything out — shouldn't I have the proposal builder
  // first?"): after the customer, two doors. Default = build the proposal
  // (price falls out of the build; accepting it creates this job). "I already
  // have the price" keeps the old direct form for kitchen-table sales.
  const [mode, setMode] = useState<"choose" | "direct">("choose");
  const [title, setTitle] = useState("System replacement");
  const [tech, setTech] = useState(""); // salesperson display name persisted to pegasus_job.sale_tech
  const [saleEmpno, setSaleEmpno] = useState<string | null>(null); // controls the dropdown
  const [price, setPrice] = useState<string>("");
  const [srcDispatch, setSrcDispatch] = useState(prefill?.sourceDispatch ?? "");

  const startProposal = () => {
    if (!picked || createQuote.isPending) return;
    createQuote.mutate(
      {
        custno: picked.custno,
        locno: picked.locno,
        quote_type: "install",
        dispatch_no: srcDispatch.trim() || null,
      },
      {
        onSuccess: (quoteId) => {
          onClose();
          navigate(`/quotes/${quoteId}`);
        },
      },
    );
  };
  // The picked customer's dispatches — browse # + date for the originating call.
  const { data: dispatches } = useCustomerDispatches(picked?.custno ?? null, picked?.locno ?? null);

  const canSave = !!picked && title.trim().length > 0 && !sell.isPending;
  const submit = () => {
    if (!picked) return;
    sell.mutate({
      custno: picked.custno, locno: picked.locno, customer_name: picked.name,
      address: picked.address, city: null, title: title.trim(),
      sale_tech: saleEmpno || tech.trim() || null, sell_price: price === "" ? null : Number(price),
      source_dispatch: srcDispatch.trim() || null,
    }, { onSuccess: (job) => onCreated(job.id) });
  };

  const inputCls =
    "w-full rounded-md border border-hairline px-3 py-2.5 text-sm text-slate-800 transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30";
  const labelCls =
    "mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-400";

  return (
    <Modal open onClose={onClose} title="Sell an Install" subtitle="Creates the job + an unscheduled install visit" size="lg">
      <div className="space-y-5">
        {/* Context strip once a customer is chosen — matches the Run-a-Call header. */}
        {picked && (
          <div className="flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2.5 text-sm text-slate-600">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <span className="min-w-0">
              <span className="font-semibold text-slate-800">{picked.name}</span>
              {picked.address ? <span className="text-slate-500"> · {picked.address}</span> : null}
            </span>
            <button
              type="button"
              onClick={() => { setPicked(null); setSrcDispatch(""); }}
              title="Change customer"
              className="ml-auto shrink-0 rounded p-0.5 text-slate-400 transition-colors hover:text-rose-500"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Customer search (only until picked) */}
        {!picked && (
          <div>
            <span className={labelCls}>Customer</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={term}
                onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                placeholder="Search name or address…"
                className={cn(inputCls, "pl-9")}
              />
              {open && term.trim().length >= 2 && (matches?.length ?? 0) > 0 && (
                <div className="absolute z-30 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-hairline bg-white py-1 shadow-lg">
                  {(matches ?? []).map((c) => (
                    <button
                      key={`${c.custno}-${c.locno}`}
                      type="button"
                      onMouseDown={() => { setPicked(c); setOpen(false); }}
                      className="block w-full truncate px-3 py-2 text-left text-sm hover:bg-brand-50"
                    >
                      <span className="font-medium text-slate-800">{c.name}</span>{" "}
                      <span className="text-xs text-slate-400">{c.address}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TWO DOORS once the customer is picked */}
        {picked && mode === "choose" && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={startProposal}
                disabled={createQuote.isPending}
                className="rounded-2xl border-2 border-brand-500 bg-brand-50/60 p-4 text-left shadow-e2 transition-all hover:bg-brand-50 disabled:opacity-60"
              >
                <div className="flex items-center gap-2 text-[15px] font-bold text-brand-900">
                  <FilePlus2 className="h-5 w-5 text-brand-600" />
                  {createQuote.isPending ? "Opening the builder…" : "Build the proposal first"}
                  <span className="rounded-full bg-brand-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">recommended</span>
                </div>
                <p className="mt-1.5 text-[13px] leading-snug text-slate-600">
                  Best / Better / Good options, priced as you build. When the
                  customer accepts, one click turns it into this install job —
                  price carries over.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setMode("direct")}
                className="rounded-2xl border-2 border-slate-200 bg-white p-4 text-left transition-all hover:border-slate-300"
              >
                <div className="flex items-center gap-2 text-[15px] font-bold text-slate-800">
                  <HandCoins className="h-5 w-5 text-slate-500" />
                  I already have the price
                </div>
                <p className="mt-1.5 text-[13px] leading-snug text-slate-600">
                  Sold at the kitchen table or priced elsewhere — enter the
                  number and create the job directly.
                </p>
              </button>
            </div>
            <div className="flex justify-start border-t border-hairline pt-3">
              <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            </div>
          </div>
        )}

        {/* Install details — DIRECT path only, and only after the customer is
            picked (Seth 2026-08-18: the form was leaking through underneath
            the search before a customer was chosen — staging bug). */}
        {picked && mode === "direct" && (
        <div className="rounded-xl border border-hairline bg-slate-50/60 p-4">
          <span className="mb-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <Package className="h-3.5 w-3.5 text-slate-400" /> Install details
          </span>

          <label className="block">
            <span className={labelCls}>Job title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={cn(inputCls, "bg-white text-base font-medium")} placeholder="System replacement" />
          </label>

          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className={labelCls}>Salesperson</span>
              <TeamMemberSelect
                value={saleEmpno}
                onChange={(empno, name) => { setSaleEmpno(empno); setTech(name ?? ""); }}
                includeBlank
                blankLabel="Select…"
                className="bg-white px-3 py-2.5 text-slate-800"
              />
            </label>
            <label className="block">
              <span className={labelCls}>Sell price</span>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className={cn(inputCls, "bg-white pl-7 text-right tabular-nums")}
                  placeholder="0"
                />
              </div>
            </label>
          </div>

          <label className="mt-3 block">
            <span className={labelCls}>Originating service call <span className="normal-case text-slate-300">(optional)</span></span>
            {!picked ? (
              <input disabled className={cn(inputCls, "cursor-not-allowed bg-slate-100 text-slate-400")} placeholder="Pick a customer first" />
            ) : (dispatches && dispatches.length > 0) ? (
              <select value={srcDispatch} onChange={(e) => setSrcDispatch(e.target.value)} className={cn(inputCls, "bg-white")}>
                <option value="">— none —</option>
                {dispatches.map((d) => (
                  <option key={d.dispatch} value={d.dispatch}>#{d.dispatch} · {d.date ? fmtDate(d.date) : "no date"}{d.status ? ` · ${d.status}` : ""}</option>
                ))}
              </select>
            ) : (
              <input value={srcDispatch} onChange={(e) => setSrcDispatch(e.target.value)} className={cn(inputCls, "bg-white")} placeholder="No dispatches on file — type a # if needed" />
            )}
          </label>
          {picked && (
            <button
              type="button"
              onClick={startProposal}
              disabled={createQuote.isPending}
              className="mt-3 text-xs font-medium text-brand-600 transition-colors hover:text-brand-700 disabled:opacity-60"
            >
              ← Actually, build the proposal first
            </button>
          )}
        </div>
        )}

        {/* Ready-to-create confirmation — echoes Run-a-Call's "Ready to schedule" card. */}
        {mode === "direct" && canSave && (
          <div className="flex items-center gap-3 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3 shadow-sm">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white shadow-sm">
              <Hammer className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-brand-500">Ready to create</div>
              <div className="truncate text-base font-bold text-slate-900">
                {title.trim()}
                <span className="px-1 font-normal text-slate-300">·</span>{picked!.name}
                {price !== "" && Number(price) > 0 && (
                  <><span className="px-1 font-normal text-slate-300">·</span>{money(Number(price))}</>
                )}
              </div>
            </div>
          </div>
        )}

        {!picked && (
          <div className="flex justify-start border-t border-hairline pt-4">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          </div>
        )}
        {picked && mode === "direct" && (
          <div className="flex items-center justify-between gap-2 border-t border-hairline pt-4">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" disabled={!canSave} onClick={submit}>
              <Check className="h-3.5 w-3.5" /> {sell.isPending ? "Creating…" : "Create install job"}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function NewConstructionModal({ onClose, onCreated }: { onClose: () => void; onCreated: (jobId: string) => void }) {
  const create = useCreateNCJob();
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [builder, setBuilder] = useState<CustomerLite | null>(null);
  const { data: matches } = useCustomerLookup(term);
  const [addr, setAddr] = useState(""); const [city, setCity] = useState("");
  const [title, setTitle] = useState("");
  const [supervisor, setSupervisor] = useState("");
  const [contract, setContract] = useState("");
  const [po, setPo] = useState("");
  // NC-DRAW-02 / PERMIT-01: the builder's defaults ride onto the new house.
  const { data: builderCfg } = useBuilder(builder?.custno ?? null);
  const { data: drawDefault } = useNcDrawDefault();
  const plan = effectiveDrawPlan(null, builderCfg, drawDefault);

  const canSave = !!builder && (title.trim().length > 0 || addr.trim().length > 0) && !create.isPending;
  const submit = () => {
    if (!builder) return;
    create.mutate({
      custno: builder.custno, customer_name: builder.name,
      site_address: addr.trim() || null, site_city: city.trim() || null,
      title: title.trim() || addr.trim() || "New-construction home",
      supervisor: supervisor.trim() || null, contract: contract === "" ? null : Number(contract),
      po_number: po.trim() || null, permit_by: builderCfg?.permit_by ?? null,
    }, { onSuccess: (job) => onCreated(job.id) });
  };

  return (
    <Modal open onClose={onClose} title="New Construction Job" subtitle="A home/lot under a builder — billed in draws" size="md">
      <div className="space-y-4">
        <JField label="Builder / GC">
          {builder ? (
            <div className="flex items-center justify-between gap-2 rounded-md border border-brand-200 bg-brand-50/50 px-2.5 py-1.5 text-sm">
              <span className="truncate text-slate-700"><span className="font-medium">{builder.name}</span></span>
              <button type="button" onClick={() => setBuilder(null)} className="text-slate-400 hover:text-rose-500"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <div className="relative">
              <input value={term} onChange={(e) => { setTerm(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)} className="jinp w-full" placeholder="search builder name" />
              {open && term.trim().length >= 2 && (matches?.length ?? 0) > 0 && (
                <div className="absolute z-30 mt-1 max-h-52 w-full overflow-y-auto rounded-md border border-hairline bg-white py-1 shadow-lg">
                  {(matches ?? []).map((c) => (
                    <button key={`${c.custno}-${c.locno}`} type="button" onMouseDown={() => { setBuilder(c); setOpen(false); }}
                      className="block w-full truncate px-2.5 py-1.5 text-left text-xs hover:bg-slate-50">
                      <span className="font-medium text-slate-700">{c.name}</span> <span className="text-slate-400">{c.address}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </JField>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2"><JField label="Home / lot address"><input value={addr} onChange={(e) => setAddr(e.target.value)} className="jinp w-full" placeholder="6320 Yosemite Ave" /></JField></div>
          <JField label="City"><input value={city} onChange={(e) => setCity(e.target.value)} className="jinp w-full" placeholder="Edina" /></JField>
        </div>
        <JField label="Job title"><input value={title} onChange={(e) => setTitle(e.target.value)} className="jinp w-full" placeholder="Lot 14 — 4br" /></JField>
        <div className="grid grid-cols-2 gap-3">
          <JField label="Supervisor"><input value={supervisor} onChange={(e) => setSupervisor(e.target.value)} className="jinp w-full" placeholder="e.g. Dan Weaver" /></JField>
          <JField label="Contract value">
            <div className="relative">
              <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
              <input type="number" inputMode="decimal" value={contract} onChange={(e) => setContract(e.target.value)} className="jinp w-full !pl-6 text-right tabular-nums" placeholder="0" />
            </div>
          </JField>
        </div>

        <JField label="Builder's PO (optional)"><input value={po} onChange={(e) => setPo(e.target.value)} className="jinp w-full" placeholder="prints on every draw" /></JField>
        {builder && (
          <p className="rounded-md bg-slate-50 px-2.5 py-2 text-xs text-slate-500">
            Draws: <b className="text-slate-700">{plan.plan.map((st) => `${st.label} ${st.pct}%`).join(" · ")}</b> ({plan.source === "builder" ? "their default" : "the company default"})
            {builderCfg?.permit_by ? <> · Permit: <b className="text-slate-700">{builderCfg.permit_by === "builder" ? "the builder pulls it" : builderCfg.permit_by === "none" ? "not needed" : "we pull it"}</b></> : null}
            {builderCfg?.po_required && !po.trim() ? <span className="ml-1 font-semibold text-amber-700">· they require a PO</span> : null}
          </p>
        )}
        <div className="flex items-center justify-end gap-2 border-t border-hairline pt-3">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" disabled={!canSave} onClick={submit}><Check className="h-3.5 w-3.5" /> {create.isPending ? "Creating…" : "Create job"}</Button>
        </div>
      </div>
      <style>{`.jinp{border:1px solid var(--hairline,#e2e8f0);border-radius:0.375rem;padding:0.45rem 0.55rem;font-size:0.875rem}.jinp:focus{outline:none;border-color:#6366f1;box-shadow:0 0 0 2px rgb(99 102 241 / 0.25)}`}</style>
    </Modal>
  );
}

/** Assign an installer + date + arrival window to an install visit. Writes to
 *  the visit's dispatch row (assigned_empno / scheduled_date / scheduled_window)
 *  so it lands on that installer's day on the board's Install view. */
function VisitAssign({ visit, jobId }: { visit: JobVisit; jobId: string }) {
  const { data: techs } = useAllActiveTechs();
  const assign = useAssignJobVisit();
  const [empno, setEmpno] = useState(visit.assigned_empno ?? "");
  const [date, setDate] = useState(visit.scheduled_date ?? "");
  const [win, setWin] = useState(visit.scheduled_window ?? "");
  const dirty =
    empno !== (visit.assigned_empno ?? "") ||
    date !== (visit.scheduled_date ?? "") ||
    win !== (visit.scheduled_window ?? "");
  const opts = (techs ?? [])
    .map((t) => ({ empno: t.empno, name: t.empname || [t.firstname, t.lastname].filter(Boolean).join(" ") || t.empno }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-hairline pt-2">
      <select value={empno} onChange={(e) => setEmpno(e.target.value)} className={cn(fieldCls, "min-w-[150px] flex-1")} aria-label="Installer">
        <option value="">Unassigned</option>
        {opts.map((o) => <option key={o.empno} value={o.empno}>{o.name}</option>)}
      </select>
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={cn(fieldCls, "w-40")} aria-label="Install date" />
      <input value={win} onChange={(e) => setWin(e.target.value)} list="pg-arrival-windows" placeholder="Window (7-8, 11-1, 12-4, or custom)" className={cn(fieldCls, "w-52")} aria-label="Arrival window" />
      <datalist id="pg-arrival-windows">
        <option value="7-8" /><option value="11-1" /><option value="12-4" />
      </datalist>
      <Button size="sm" variant="secondary" disabled={!dirty || assign.isPending}
        onClick={() => assign.mutate({ id: visit.id, job_id: jobId, assigned_empno: empno || null, scheduled_date: date || null, scheduled_window: win || null })}>
        {assign.isPending ? "Saving…" : "Save"}
      </Button>
    </div>
  );
}

function roleLabel(r: string) { return r === "tech_lead" ? "Tech lead" : "Salesperson"; }

/** Who gets sold-by credit on the job: the primary (pegasus_job.sale_tech) plus any
 *  secondary salesperson / tech-lead with an optional split %. Structured data the
 *  (held) commission engine reads; primary implicitly gets the remaining share. */
function SalesCreditsEditor({ jobId, saleTech }: { jobId: string; saleTech: string | null }) {
  const memberName = useMemberName();
  const { data: credits = [] } = useJobSalesCredits(jobId);
  const add = useAddSalesCredit();
  const del = useDeleteSalesCredit();
  const [adding, setAdding] = useState(false);
  const [empno, setEmpno] = useState<string | null>(null);
  const [pname, setPname] = useState<string | null>(null);
  const [role, setRole] = useState("salesperson");
  const [split, setSplit] = useState("");
  const reset = () => { setAdding(false); setEmpno(null); setPname(null); setRole("salesperson"); setSplit(""); };
  const save = () => {
    if (!empno) return;
    add.mutate({ job_id: jobId, empno, name: pname, role, split_pct: split === "" ? null : Number(split) }, { onSuccess: reset });
  };
  return (
    <div className="mt-3 border-t border-hairline pt-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Sales credit
          <InfoTip label="About sales credit">The primary salesperson gets whatever share isn&apos;t split off. Splits feed commissions.</InfoTip>
        </span>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline">
            <Plus className="h-3.5 w-3.5" /> Add salesperson
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-xs text-brand-800">
          {memberName(saleTech) ?? "—"} <span className="text-brand-400">· Primary</span>
        </span>
        {credits.map((c) => (
          <span key={c.id} className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-white px-2.5 py-1 text-xs text-slate-600">
            {c.name ?? memberName(c.empno) ?? c.empno}
            <span className="text-slate-400">· {roleLabel(c.role)}{c.split_pct != null ? ` · ${c.split_pct}%` : ""}</span>
            <button type="button" onClick={() => del.mutate({ id: c.id, job_id: jobId })} title="Remove" className="rounded p-0.5 text-slate-300 hover:bg-rose-50 hover:text-rose-500"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      {adding && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <TeamMemberSelect value={empno} onChange={(e, n) => { setEmpno(e); setPname(n); }} blankLabel="Choose person…" className="w-48" />
          <select value={role} onChange={(e) => setRole(e.target.value)} className="rounded-md border border-hairline px-2 py-1.5 text-sm">
            <option value="salesperson">Salesperson</option>
            <option value="tech_lead">Tech lead</option>
          </select>
          <div className="relative w-24">
            <input type="number" value={split} onChange={(e) => setSplit(e.target.value)} placeholder="split" className="w-full rounded-md border border-hairline px-2 py-1.5 pr-6 text-right text-sm tabular-nums" />
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
          </div>
          <Button size="sm" variant="secondary" onClick={save} disabled={!empno || add.isPending}>Add</Button>
          <button type="button" onClick={reset} className="text-xs text-slate-500 hover:underline">Cancel</button>
        </div>
      )}
    </div>
  );
}


/** Live job costing for the header strip — same math as CostingSection
 *  (React Query dedupes the underlying queries, so this is free). */
function useJobLiveCosting(jobId: string, sellPrice: number | null) {
  const { data: parts } = useJobParts(jobId);
  const { data: equip } = useJobEquipment(jobId);
  const { data: labor } = useJobLabor(jobId);
  const { data: permits } = useJobPermits(jobId);
  const { data: cos } = useChangeOrders(jobId);
  const { data: jobMats } = useJobMaterials(jobId);
  // REFRIG-01: the pounds added on the job (the factory charge came with the unit).
  const { data: refLogs } = useJobRefrigerant(jobId);
  const { data: refItems } = useRefrigerantItems();
  const actual =
    (parts ?? []).reduce((s, x) => s + x.qty * x.unit_cost, 0) +
    (jobMats ?? []).reduce((s, m) => s + materialCost(m), 0) +
    (equip ?? []).reduce((s, e) => s + (e.unit_cost ?? 0) * (e.qty || 1), 0) +
    (labor ?? []).reduce((s, l) => s + Number(l.hours || 0) * Number(l.rate || 0), 0) +
    (permits ?? []).reduce((s, x) => s + (x.by_builder || x.status === "cancelled" ? 0 : (x.fee ?? 0)), 0) +
    refrigerantCost(refLogs ?? [], refItems);
  const approvedCO = (cos ?? []).filter((c) => c.status === "approved").reduce((s, c) => s + Number(c.amount || 0), 0);
  const quoted = (sellPrice ?? 0) + approvedCO;
  return { actual, quoted, margin: quoted - actual, pct: quoted > 0 ? (quoted - actual) / quoted : null };
}

/** JOB-NAV-01 (Seth 2026-09-25: "the top navigation bar with the steps that
 *  complete and then the menu bar further down with the same thing - it's all
 *  confusing. It's really hard to even know there is that secondary navigation
 *  bar"). ONE bar: every stage says whether it's done and what's left, and
 *  clicking it opens that stage's work right below — the Salesforce Path used as
 *  the tabs; ServiceTitan / Jobber / Housecall Pro never show the stages twice.
 *  Looking isn't moving: the stage the job is AT says "Now", the one you're
 *  LOOKING at is the raised tab. ?tab= deep links keep working (old names too). */
type Stage = "sold" | "workorder" | "scheduled" | "permit" | "warehouse" | "installed" | "post" | "closed" | "summary";
const STAGE_FROM_PARAM: Record<string, Stage> = {
  sold: "sold", workorder: "workorder", schedule: "scheduled", scheduled: "scheduled", permits: "permit", permit: "permit",
  warehouse: "warehouse", installed: "installed", postinstall: "post", post: "post", money: "closed", closed: "closed", summary: "summary",
};
const LIFE_STAGES = ["sold", "workorder", "scheduled", "permit", "warehouse", "installed", "post", "closed"] as const;
type LifeStage = (typeof LIFE_STAGES)[number];
const LIFE_STAGE_NAME: Record<LifeStage, string> = {
  sold: "Sold", workorder: "Work Order", scheduled: "Scheduled", permit: "Permit",
  warehouse: "Warehouse", installed: "Installed", post: "Post-Install", closed: "Closed",
};
/** "Sep 25" (the year only when it isn't this year). */
const fmtDay = (s: string | null | undefined) => {
  if (!s) return "";
  const d = new Date(s.length <= 10 ? `${s}T12:00:00` : s);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(d.getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}) });
};
/** The calendar day a timestamp falls on in Minnesota (matches close_job). */
const chicagoDay = (ts: string) => new Date(ts).toLocaleDateString("en-CA", { timeZone: "America/Chicago" });

export function JobWorkspace() {
  const { jobId = "" } = useParams();
  const navigate = useNavigate();
  const { data: job, isLoading } = useJob(jobId);
  // FIELD-CREWS-01: what the crew does on the iPad (units confirmed, a serial
  // changed, materials used, an add-on the customer signed, the old unit out)
  // lands on this page within seconds.
  useRealtimeRefresh(
    ["pegasus_job", "pegasus_job_equipment", "pegasus_job_material", "pegasus_change_order", "pegasus_job_removed_equipment",
      "pegasus_refrigerant_log", "pegasus_job_check", "pegasus_rebate_test", "pegasus_material_request", "pegasus_job_ahri",
      "pegasus_install_question", "pegasus_install_section"],
    [["job"], ["jobs"], ["job_equipment"], ["job_materials"], ["change_orders"], ["job_money"], ["job_removed_equipment"], ["internal_notes"],
      ["job_refrigerant"], ["job_checks"], ["rebate_test"], ["rebate_test_status"], ["material_requests"], ["material_requests_open"], ["job_ahri"],
      ["install_bank"]],
  );
  const { data: visits } = useJobVisits(jobId);
  // Derived-status inputs — this job's permits + equipment. Called by jobId (not
  // job.id) and BEFORE the early return so hook order never changes between renders.
  const { data: hdrPermits } = useJobPermits(jobId);
  const { data: hdrEquip } = useJobEquipment(jobId);
  const { data: jobQuote } = useJobQuote(jobId);
  const { data: matReqs } = useJobMaterialRequests(jobId);
  // JOURNEY-02 (Seth 2026-08-25): "Work order before schedule" — the packet
  // of files/pictures IS the work order being built; any file on the job
  // lights the station.
  const { data: woFiles } = useJobFiles(jobId);
  const woCount = (woFiles ?? []).length;
  // INSTALL-SPEC-02 / WO-DONE-01: the equipment drives the install-detail
  // questions; the Work Order is done when the equipment is listed and every
  // question that fits it is answered. SUB-SEND-02: the sub's copy + date.
  const equipFacts = useJobEquipFacts(hdrEquip);
  const installBank = useInstallBank();
  const { data: jobSends } = useJobSends(jobId);
  const { data: subsList } = useSubcontractors();
  // JOB-SOLD-01: the Sold stage is done when the equipment was checked against the load calc.
  const { rules: lcRules } = useLoadCalcRules();
  const personOf = usePersonName();
  // HOOKS-FIX (Seth 2026-08-25 "Rendered more hooks than during the previous
  // render"): this MUST run before the early return below — on a first,
  // uncached open the job starts undefined, and a hook that only runs once
  // the job loads changes the hook count between renders (the crash).
  const { data: postInstall } = usePostInstall({ dispatch: job?.source_dispatch ?? null, jobId: jobId || null });
  const memberName = useMemberName();
  const upd = useUpdateJob();
  const del = useDeleteJob();
  // PERM-ENFORCE-01: erasing a (test) job is its own power (jobs.delete).
  const canDeleteJob = useCan("jobs.delete");
  const createVisit = useCreateInstallVisit();
  const toggleCheck = useToggleJobCheck();
  const { data: benchmarks } = useJobBenchmarks(jobId);
  const addBench = useAddJobBenchmark();
  const toggleBench = useToggleJobBenchmark();
  const delBench = useDeleteJobBenchmark();
  const [benchInput, setBenchInput] = useState("");
  const [editing, setEditing] = useState(false);
  const [d, setD] = useState({ title: "", sale_tech: "", sell_price: "", status: "sold", is_test: false });
  // The stage being LOOKED at. null = the default: Work Order, or the Summary
  // once the job is closed. ?tab= deep-links a stage (the Warehouse queue sends
  // crews straight to the pull sheet).
  const [stagePick, setStagePick] = useState<Stage | null>(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return t && STAGE_FROM_PARAM[t] ? STAGE_FROM_PARAM[t] : null;
  });
  const panelTop = useRef<HTMLDivElement>(null);
  // JOB-SOLD-01: the stage a job opens on is decided once (Sold until the sale is checked) — confirming the
  // equipment doesn't yank the page to Work Order under the person who just clicked.
  const openedOn = useRef<{ id: string; stage: Stage } | null>(null);
  const costing = useJobLiveCosting(jobId, job?.sell_price ?? null);
  // Customer-facing invoice message (prints on the install invoice); synced from the job.
  const [invMsg, setInvMsg] = useState("");
  useEffect(() => { setInvMsg(job?.invoice_message ?? ""); }, [job?.id, job?.invoice_message]);
  // The customer's self-service portal link, so the office can preview exactly what they see.
  const { data: portalLink } = useQuery({
    queryKey: ["job_portal_link", job?.custno],
    enabled: !!job?.custno,
    queryFn: async () => {
      const tok = await supabase.rpc("get_customer_link_token", { p_custno: job!.custno });
      const base = /localhost|127\.0\.0\.1/.test(window.location.hostname) ? window.location.origin : "https://my.sedgwickheating.com";
      return `${base}/portal?t=${tok.data ?? ""}`;
    },
  });

  if (!job) return (
    <div className="jobpage mx-auto max-w-4xl px-6 py-10">
      <button type="button" onClick={() => navigate("/jobs")} className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-brand-700"><ArrowLeft className="h-4 w-4" /> Jobs</button>
      <div className="rounded-xl border border-hairline bg-white p-10 text-center text-sm text-slate-400">{isLoading ? "Loading job…" : "Job not found."}</div>
    </div>
  );

  const startEdit = () => {
    setD({ title: job.title ?? "", sale_tech: job.sale_tech ?? "", sell_price: job.sell_price?.toString() ?? "", status: job.status, is_test: !!job.is_test });
    setEditing(true);
  };
  const saveEdit = () => upd.mutate({
    id: job.id, title: d.title.trim() || null, sale_tech: d.sale_tech.trim() || null,
    sell_price: d.sell_price === "" ? null : Number(d.sell_price), status: d.status, is_test: d.is_test,
  }, { onSuccess: () => setEditing(false) });

  const closed = job.status === "closed";
  const cancelled = job.status === "cancelled";
  let view: Stage = stagePick ?? (closed ? "summary" : "workorder");
  if (!stagePick && openedOn.current?.id === job.id) view = openedOn.current.stage;
  // Status reads itself from real work done — not a bubble someone clicks (Seth
  // 2026-07-07: the stage pills + "Advance to" made no sense; derive, don't flag).
  // POST-INSTALL-02: "No Permit Needed" (set on the Permits step or at sale
  // time) counts as the permit step being DONE — the job stops nagging.
  // PERMIT-01: who pulls it governs the step — builder / not needed = done;
  // otherwise one of OUR permits must be on file (Requested doesn't count).
  const permitBy = effectivePermitBy(job, postInstall ?? null);
  const permitWaived = permitBy === "builder" || permitBy === "none";
  const permitsOnFile = (hdrPermits ?? []).filter(permitIsOnFile).length;
  const permitRequested = (hdrPermits ?? []).some((p) => p.status === "requested" && !p.by_builder);
  const hasPermit = permitsOnFile > 0 || permitWaived;
  const scheduled = (visits ?? []).some((v) => !!v.scheduled_date);
  const units = hdrEquip ?? [];
  const serialCount = units.filter((e) => e.serial && e.serial.trim()).length;
  const pulledCount = units.filter((e) => !!e.pulled_at).length;
  // MATERIAL-REQ-01: a crew request still being pulled or on order keeps the warehouse open.
  const reqLinesOpen = (matReqs ?? []).filter((r) => r.status === "open").flatMap((r) => r.lines)
    .filter((l) => l.pull_status && !["ready", "picked_up", "cancelled"].includes(l.pull_status)).length;
  const pulledAll = units.length > 0 && pulledCount === units.length && reqLinesOpen === 0;
  // Installed = the crew said so (Install Complete on the phone) or confirmed every unit.
  const crewDone = !!job.crew_finished_at || (units.length > 0 && units.every((e) => !!e.install_confirmed_at));
  const ready = serialCount > 0 && hasPermit;
  // Benchmarks tick automatically from real work; the office can also hand-tick
  // any the system hasn't detected (manual_checks). Effective done = derived OR manual.
  const manualChecks: Record<string, boolean> = job.manual_checks ?? {};
  // JOURNEY-01: the Post-Install station is a first-class stage — the five
  // customer sends (permit info, survey, referral, warranty cert, rebate).
  // sent OR not-needed = handled; the waived-permit switch counts too.
  const pi = postInstall ?? null;
  const piStatuses: string[] = [
    permitWaived ? "not_needed" : (pi?.permit_status ?? "pending"),
    pi && !pi.reviews_enabled ? "not_needed" : (pi?.survey_status ?? "pending"),
    pi?.referral_status ?? "pending",
    pi?.warranty_status ?? "pending",
    pi?.rebate_status ?? "pending",
  ];
  const piTotal = piStatuses.length;
  const piDone = piStatuses.filter((st) => st === "sent" || st === "not_needed").length;
  // WO-DONE-01 (Seth 2026-09-25: "Equipment should be the first to be
  // selected. Then the install details should be tailored around that"): the
  // plan is done when the equipment is on the job and every install detail
  // that fits it is answered. New construction keeps the files rule (plans).
  const specProg = specProgress(job.install_spec, equipFacts, installBank);
  const woDone = isNC(job) ? woCount > 0 || units.length > 0 : units.length > 0 && specProg.complete;
  const specOpen = specProg.total - specProg.answered;
  // SUB-SEND-02: the work order goes to the sub once there's a date; a moved date means resend.
  const firstDay = firstInstallDay(visits ?? []);
  const lastSend = (jobSends ?? [])[0] ?? null;
  const noSubs = !!manualChecks["no_subs"];
  const activeSubsList = (subsList ?? []).filter((s) => s.active);
  const subLabel = activeSubsList.length === 1 ? activeSubsList[0].name : "the Subcontractor";
  const subState = subSendState(lastSend, firstDay);
  // JOB-SOLD-01 (Seth 2026-09-28: "Then we verify and enter the proper equipment based on the load calc"):
  // Sold is done once someone confirms the units against the load calc (new construction builds from plans).
  const saleLoad = effectiveLoad(job, woFiles);
  const sold = soldState(job, hdrEquip, saleLoad, lcRules);
  const soldDone = isNC(job) || sold.done;
  const derivedByKey: Record<LifeStage, boolean> = {
    sold: soldDone, workorder: woDone, scheduled, permit: hasPermit, warehouse: pulledAll, installed: crewDone, post: piDone === piTotal, closed,
  };
  const visitDays = (visits ?? []).map((v) => v.scheduled_date).filter(Boolean).sort() as string[];
  const installDay = job.install_date ?? (job.crew_finished_at ? chicagoDay(job.crew_finished_at) : null);
  const stageNote: Record<LifeStage, string> = {
    sold: isNC(job) || (soldDone && !job.equip_confirmed_at) ? fmtDay(job.created_at)
      : soldDone ? `Checked ${fmtDay(job.equip_confirmed_at)}`
      : sold.next === "add_equipment" ? "Add equipment"
      : sold.next === "load_calc" ? "Load calc needed"
      : sold.stale ? "Units changed"
      : "Check equipment",
    workorder: isNC(job)
      ? (woCount > 0 ? `${woCount} file${woCount === 1 ? "" : "s"}` : units.length > 0 ? `${units.length} unit${units.length === 1 ? "" : "s"}` : "")
      : units.length === 0 ? "Add equipment" : specOpen > 0 ? `Guide: ${specOpen} open` : `${units.length} unit${units.length === 1 ? "" : "s"} · guide done`,
    scheduled: visitDays[0] ? fmtDay(visitDays[0]) : "",
    permit: permitWaived && permitsOnFile === 0 ? (permitBy === "builder" ? "Builder pulls" : "No permit")
      : permitsOnFile > 0 ? `${permitsOnFile} on file` : permitRequested ? "Requested" : "",
    warehouse: units.length === 0 ? "" : `${pulledCount} of ${units.length} pulled${reqLinesOpen ? ` · ${reqLinesOpen} requested` : ""}`,
    installed: installDay ? fmtDay(installDay) : "",
    post: `${piDone} of ${piTotal} sent`,
    closed: job.closed_at ? fmtDay(job.closed_at) : "",
  };
  // Rail order mirrors the REAL ritual (Seth 2026-08-21 walkthrough + 2026-08-25
  // "Work order before schedule"): sell it, build the work order, schedule it,
  // pull the permit, pull the materials, install it, do the after-care, close it.
  const progress = LIFE_STAGES.map((key) => {
    const derived = !!derivedByKey[key];
    return { key, label: LIFE_STAGE_NAME[key], derived, done: derived || !!manualChecks[key] };
  });
  const onToggleLifecycle = (key: string) => {
    const cur = { ...manualChecks, [key]: !manualChecks[key] };
    toggleCheck.mutate({ id: job.id, manual_checks: cur });
  };
  /** Open a stage; with a zone, land on that card inside it. */
  const go = (s: Stage, zone?: string) => {
    setStagePick(s);
    // Keep the stage in the address so a refresh (or a copied link) lands on it.
    try { const u = new URL(window.location.href); u.searchParams.set("tab", s); window.history.replaceState(window.history.state, "", u); } catch { /* ignore */ }
    window.setTimeout(() => {
      const el = zone ? document.getElementById(zone) : panelTop.current;
      if (!el) return;
      // GO-FOCUS-01 (Seth 2026-09-28: "I can't add equipment by clicking the add
      // equipment … It's like nothing is working"): a jump that lands on a form
      // puts the cursor in it and flashes it — scrolling alone looked like nothing
      // happened when the card was already on screen.
      const form = el.hasAttribute("data-go-focus");
      if (zone || el.getBoundingClientRect().top < 150) el.scrollIntoView({ behavior: "smooth", block: form ? "center" : "start" });
      if (form) {
        el.querySelector<HTMLInputElement>("input:not([type=file]):not([type=hidden])")?.focus({ preventScroll: true });
        el.classList.remove("go-flash");
        void el.offsetWidth;
        el.classList.add("go-flash");
        window.setTimeout(() => el.classList.remove("go-flash"), 1600);
      }
    }, 60);
  };
  // SUB-AUTO-RESEND: a sub holding the wrong day outranks everything else —
  // that's a crew at the wrong house on the wrong day. Details can follow.
  const subStale = !isNC(job) && !noSubs && !!lastSend && (subState === "unscheduled" || subState === "moved" || subState === "needs_date");
  const nextStep: { label: string; stage: Stage; zone?: string } | null = closed || cancelled ? null
    : subStale && subState === "unscheduled" ? { label: `Tell ${lastSend?.to_name ?? "the Sub"} It's Postponed`, stage: "scheduled", zone: "zone-sub" }
    : subStale ? { label: `Send ${lastSend?.to_name ?? "the Sub"} the New Date`, stage: "scheduled", zone: "zone-sub" }
    : !soldDone && !manualChecks["sold"]
      ? (sold.next === "add_equipment" ? { label: "Add the Equipment", stage: "sold", zone: "zone-equip-check" }
        : sold.next === "load_calc" ? { label: "Do the Load Calc", stage: "sold", zone: "zone-load-calc" }
        : sold.next === "reconfirm" ? { label: "Confirm the Equipment Again", stage: "sold", zone: "zone-equip-check" }
        : { label: "Confirm the Equipment", stage: "sold", zone: "zone-equip-check" })
    : !woDone && !manualChecks["workorder"]
      ? (isNC(job) ? { label: "Build the Work Order", stage: "workorder" }
        : units.length === 0 ? { label: "Add the Equipment", stage: "workorder", zone: "zone-equipment-add" }
        : { label: `Finish the Installer Guide (${specOpen} Open)`, stage: "workorder", zone: "zone-install-details" })
    : !scheduled ? { label: "Schedule the Install", stage: "scheduled", zone: "zone-visits" }
    : !isNC(job) && !noSubs && !lastSend ? { label: `Send the Work Order to ${subLabel}`, stage: "scheduled", zone: "zone-sub" }
    : !hasPermit ? { label: "Add the Permit", stage: "permit", zone: "zone-permit" }
    : serialCount === 0 ? { label: "Log the Serials", stage: "workorder", zone: "zone-equipment" }
    : piDone < piTotal ? { label: `Send the Post-Install Items (${piDone} of ${piTotal})`, stage: "post" }
    : { label: "Close Out the Job", stage: "closed", zone: "zone-closeout" };
  // JOB-SOLD-01: a job whose sale hasn't been checked opens on Sold (decided once the units and files are in).
  const soldReady = hdrEquip !== undefined && woFiles !== undefined;
  if (!stagePick && openedOn.current?.id !== job.id) {
    const guessDone = soldReady ? soldDone : isNC(job) || !!job.equip_confirmed_at;
    if (!closed && !cancelled && !guessDone && !manualChecks["sold"]) view = "sold";
    if (soldReady) openedOn.current = { id: job.id, stage: view };
  }
  const derivedLabel = cancelled ? "Cancelled" : closed ? "Closed" : ready ? "Ready to Close"
    : serialCount > 0 ? "Installing" : scheduled ? "Scheduled" : hasPermit ? "Permitted" : "Sold";
  // A closed or cancelled job isn't "at" any stage any more — no Now marker.
  const currentKey = cancelled || closed ? null : progress.find((p) => !p.done)?.key ?? null;

  // DELETE-JOB-01 via TEST-FLAG-01: only a job marked Test can be deleted — a
  // real job is cancelled (Edit → Stage), never erased.
  const footer = job.is_test && canDeleteJob ? (
    <div className="flex items-center justify-between gap-2">
      <Button variant="ghost" size="sm" className="text-rose-600" disabled={del.isPending}
        onClick={() => { if (confirm("Delete this test job and its visits?")) del.mutate(job.id, { onSuccess: () => navigate("/jobs") }); }}>
        <Trash2 className="h-3.5 w-3.5" /> Delete Test Job
      </Button>
    </div>
  ) : null;

  return (
    <div className="jobpage mx-auto max-w-[1400px] px-6 py-6">
      <button type="button" onClick={() => navigate("/jobs")} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-slate-500 transition-colors hover:text-brand-700"><ArrowLeft className="h-4 w-4" /> Jobs</button>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
        <div className="min-w-0">
          {/* JOURNEY-01 (Seth 2026-08-21: "the address needs to look larger and
              more prominent and the install scheduled date more featured") —
              the ADDRESS is the headline; the install date wears a real chip. */}
          <p className="text-base font-medium text-slate-500">{job.customer_name ?? job.custno}{job.title ? ` · ${job.title}` : ""}</p>
          <h1 className="mt-0.5 flex items-center gap-2 text-3xl font-bold leading-tight tracking-tight text-slate-900">
            <MapPin className="h-7 w-7 shrink-0 text-brand-500" />
            <span className="min-w-0">{job.address ?? job.title ?? "Install"}</span>
          </h1>
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            {/* The number everyone talks about on the phone — front and center. */}
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-1.5 text-[15px] font-bold tabular-nums text-white"
              title={job.source_dispatch ? "The ESC dispatch this job came from" : "Pegasus job number"}>
              <Hammer className="h-4 w-4 text-white/70" />
              {job.source_dispatch ? `Dispatch #${job.source_dispatch}` : `Job #${job.id.slice(0, 6).toUpperCase()}`}
            </span>
            {job.is_test && (
              <span title="A test job never messages anyone — no customer texts or emails, no work order to the sub"
                className="inline-flex items-center gap-1.5 rounded-lg bg-rose-50 px-3 py-1.5 text-[15px] font-bold text-rose-700 ring-1 ring-inset ring-rose-200">
                <FlaskConical className="h-4 w-4" /> Test Job
              </span>
            )}
            {visitDays[0] ? (
              <span className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-1.5 text-[15px] font-bold text-white shadow-e1">
                <CalendarClock className="h-4 w-4" />
                Install {new Date(`${visitDays[0]}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
              </span>
            ) : !closed && !cancelled ? (
              <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-1.5 text-[15px] font-bold text-amber-700 ring-1 ring-amber-200">
                <CalendarClock className="h-4 w-4" /> Not Scheduled Yet
              </span>
            ) : null}
          </div>
          {/* THE SALE, always visible (Seth 2026-08-18: sold details shouldn't
              need a tab; PLAN-SHOW-01: the office must see which financing plan
              to process) — one line, linking back to where it came from. */}
          <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[15px] text-slate-600">
            <span>Sold <strong className="text-slate-800">{fmtDate(job.created_at)}</strong>{job.sale_tech ? <> by <strong className="text-slate-800">{memberName(job.sale_tech) ?? job.sale_tech}</strong></> : null}</span>
            <SalePaymentLine quote={jobQuote} />
            {job.source_dispatch && (
              <Link to={`/dispatch/${job.source_dispatch}`} className="font-medium text-brand-700 hover:underline">From service call #{job.source_dispatch}</Link>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-3">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {jobQuote && (
              <Link to={`/quotes/${jobQuote.id}`}
                title="What the sales tech quoted — cross-reference what was promised to the customer"
                className="inline-flex h-9 items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 px-3.5 text-sm font-bold text-brand-700 transition-colors hover:bg-brand-100">
                <FileText className="h-4 w-4" /> View Proposal{jobQuote.accepted_at ? " (Accepted)" : ""}
              </Link>
            )}
            {job.custno && <Link to={`/customers/${job.custno}`} className="inline-flex h-9 items-center gap-1 rounded-full px-3 text-sm font-semibold text-brand-700 hover:bg-brand-50"><User className="h-4 w-4" /> Customer 360</Link>}
            {portalLink && <a href={portalLink} target="_blank" rel="noreferrer" title="See exactly what the customer sees in their portal" className="inline-flex h-9 items-center gap-1 rounded-full px-3 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-brand-700"><ExternalLink className="h-4 w-4" /> Preview Portal</a>}
            {!editing ? (
              <Button variant="secondary" onClick={startEdit}><Pencil className="h-4 w-4" /> Edit</Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
                <Button onClick={saveEdit} disabled={upd.isPending}><Check className="h-4 w-4" /> {upd.isPending ? "Saving…" : "Save"}</Button>
              </div>
            )}
          </div>
          {/* JOB-PAGE-02 — the LIVE MONEY numbers: contract vs cost-so-far vs
              margin, updating as equipment/labor land (Run a Call's "updates
              live as the tech adds parts"). Click through to the breakdown. */}
          <button type="button" onClick={() => go("closed", "zone-costing")} title="Open the full cost breakdown"
            className="flex items-end gap-7 rounded-xl border border-hairline bg-white px-5 py-3 text-left transition-colors hover:border-slate-300">
            <span>
              <span className="block text-sm font-semibold text-slate-500">Contract</span>
              <span className="block text-2xl font-bold leading-tight tabular-nums text-slate-900">{money(costing.quoted)}</span>
            </span>
            <span>
              <span className="block text-sm font-semibold text-slate-500">Cost So Far</span>
              <span className="block text-2xl font-bold leading-tight tabular-nums text-slate-700">{money(costing.actual)}</span>
            </span>
            <span>
              <span className="block text-sm font-semibold text-slate-500">Margin</span>
              <span className={cn("block text-2xl font-bold leading-tight tabular-nums", costing.margin >= 0 ? "text-emerald-700" : "text-rose-600")}>
                {money(costing.margin)}
                {costing.pct != null && <span className="ml-1.5 align-middle text-base font-semibold text-slate-400">{Math.round(costing.pct * 100)}%</span>}
              </span>
            </span>
          </button>
        </div>
      </header>

      {/* THE ONE BAR — the stages ARE the navigation. Sticky under the top bar
          so it's always there on a long Work Order. top-0, not top-16: <main>'s
          own pt-16 already clears the floating top bar (sticky counts padding). */}
      <nav aria-label="Job stages" className="sticky top-0 z-20 -mx-3 mb-3 bg-canvas/95 px-3 py-2 backdrop-blur-md">
        <div className="flex gap-1.5 overflow-x-auto pb-0.5">
          {progress.map((s) => {
            const current = s.key === currentKey;
            const selected = view === s.key;
            const note = stageNote[s.key];
            return (
              <div key={s.key} className="group/stage relative min-w-fit flex-1">
                <button
                  type="button"
                  onClick={() => go(s.key)}
                  aria-current={selected ? "page" : undefined}
                  className={cn(
                    "flex h-full w-full flex-col items-start rounded-xl px-2.5 py-2.5 text-left transition-all xl:px-3",
                    selected
                      ? "bg-white shadow-e2 ring-2 ring-inset ring-brand-600"
                      : "bg-white/60 ring-1 ring-inset ring-hairline hover:bg-white hover:ring-slate-300",
                  )}
                >
                  <span className="flex items-center gap-1.5 text-[15px] font-bold leading-tight text-slate-900">
                    {s.done
                      ? <CheckCircle2 className="h-[18px] w-[18px] shrink-0 text-emerald-600" />
                      : current
                        ? <span className="mx-[3px] h-3 w-3 shrink-0 rounded-full bg-brand-600 ring-4 ring-brand-100" />
                        : <span className="h-4 w-4 shrink-0 rounded-full border-2 border-slate-300" />}
                    <span className="whitespace-nowrap">{s.label}</span>
                  </span>
                  <span className={cn("mt-1 min-h-[1.1rem] whitespace-nowrap text-[13px] leading-tight tabular-nums",
                    current ? "font-semibold text-brand-700" : "text-slate-500")}>
                    {current ? `Now${note ? ` · ${note}` : ""}` : note || (s.done ? "Done" : "")}
                  </span>
                </button>
                {/* Hand-tick in the corner for a stage Pegasus can't see yet — the
                    block itself navigates (Seth 2026-08-21: clicking should "take
                    you to the area"). */}
                {!s.derived && !closed && s.key !== "closed" && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onToggleLifecycle(s.key); }}
                    title={s.done ? "Marked done by hand — click to undo" : "Mark done by hand"}
                    className={cn(
                      "absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-md border transition-all",
                      s.done
                        ? "border-emerald-300 bg-emerald-50 text-emerald-700 opacity-80 hover:opacity-100"
                        : "border-slate-300 bg-white text-transparent opacity-0 hover:border-brand-400 hover:text-brand-500 group-hover/stage:opacity-100",
                    )}
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
          <div className="mx-0.5 w-px shrink-0 self-stretch bg-hairline" />
          <button
            type="button"
            onClick={() => go("summary")}
            aria-current={view === "summary" ? "page" : undefined}
            className={cn(
              "flex min-w-fit shrink-0 flex-col items-start rounded-xl px-2.5 py-2.5 text-left transition-all xl:px-3",
              view === "summary"
                ? "bg-white shadow-e2 ring-2 ring-inset ring-brand-600"
                : "bg-white/60 ring-1 ring-inset ring-hairline hover:bg-white hover:ring-slate-300",
            )}
          >
            <span className="flex items-center gap-1.5 text-[15px] font-bold leading-tight text-slate-900">
              <ClipboardCheck className="h-[18px] w-[18px] text-slate-500" /> Summary
            </span>
            <span className="mt-1 text-[13px] leading-tight text-slate-500">The whole job</span>
          </button>
        </div>
      </nav>

      {/* What's next — one line, one button — plus the office's own checklist. */}
      <div className="mb-5 rounded-xl border border-hairline bg-white px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className={cn("inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold",
            cancelled ? "bg-rose-50 text-rose-600" : closed ? "bg-emerald-50 text-emerald-700" : "bg-brand-50 text-brand-700")}>
            <span className={cn("h-2 w-2 rounded-full", cancelled ? "bg-rose-400" : closed ? "bg-emerald-500" : "bg-brand-500")} />
            {derivedLabel}
          </span>
          {nextStep
            ? <span className="text-[15px] text-slate-600">Next: <button type="button" onClick={() => go(nextStep.stage, nextStep.zone)} className="font-semibold text-brand-700 hover:underline">{nextStep.label}</button></span>
            : <span className="text-[15px] text-slate-600">{cancelled ? "This job was cancelled." : "Equipment posted to the customer’s file."}</span>}
          {nextStep && (
            <Button className="ml-auto" onClick={() => go(nextStep.stage, nextStep.zone)}>
              {nextStep.label} <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
        {/* Custom, office-defined checklist items — tick, add, remove (auto steps live above). */}
        {((benchmarks ?? []).length > 0 || !closed) && (
          <div className="mt-2.5 border-t border-hairline pt-2">
            {(benchmarks ?? []).map((b) => (
              <div key={b.id} className="group flex items-center gap-2 py-0.5">
                <button type="button" disabled={closed} onClick={() => toggleBench.mutate({ id: b.id, job_id: job.id, done: !b.done })}
                  className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors", b.done ? "bg-brand-600 text-white" : "border border-slate-300 bg-white text-transparent hover:border-brand-400")}>
                  <Check className="h-3.5 w-3.5" />
                </button>
                <span className={cn("min-w-0 flex-1 truncate text-sm", b.done ? "text-slate-400 line-through" : "text-slate-700")}>{b.label}</span>
                {!closed && <button type="button" onClick={() => delBench.mutate({ id: b.id, job_id: job.id })} title="Remove" className="rounded p-0.5 text-slate-300 opacity-0 transition-all hover:text-rose-500 group-hover:opacity-100"><X className="h-4 w-4" /></button>}
              </div>
            ))}
            {!closed && (
              <form onSubmit={(e) => { e.preventDefault(); if (benchInput.trim()) addBench.mutate({ job_id: job.id, label: benchInput }, { onSuccess: () => setBenchInput("") }); }} className="mt-1 flex items-center gap-2">
                <Plus className="h-4 w-4 shrink-0 text-slate-400" />
                <input value={benchInput} onChange={(e) => setBenchInput(e.target.value)} placeholder="Add a checklist item for this job…" className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-0" />
                {benchInput.trim() && <button type="submit" className="shrink-0 text-sm font-semibold text-brand-700 hover:underline">Add</button>}
              </form>
            )}
          </div>
        )}
      </div>

      {/* Editing the sale facts — full-width card, any stage. */}
      {editing && (
        <div className="mb-5 rounded-xl border border-brand-200 bg-white p-4">
          <div className="space-y-3">
            <JField label="Job title"><input value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} className="jinp w-full" /></JField>
            <div className="grid grid-cols-2 gap-3">
              <JField label="Sold by"><TeamMemberSelect value={d.sale_tech || null} onChange={(empno) => setD({ ...d, sale_tech: empno ?? "" })} blankLabel="Choose salesperson…" /></JField>
              <JField label="Sell price">
                <div className="relative">
                  <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                  <input type="number" value={d.sell_price} onChange={(e) => setD({ ...d, sell_price: e.target.value })} className="jinp w-full !pl-6 text-right tabular-nums" />
                </div>
              </JField>
            </div>
            <JField label="Stage">
              <span className="flex items-center gap-2">
                <select value={d.status} onChange={(e) => setD({ ...d, status: e.target.value })} className="jinp w-full">
                  {JOB_STAGES.filter((st) => st !== "closed").map((st) => <option key={st} value={st}>{STAGE_LABEL[st]}</option>)}
                  <option value="cancelled">Cancelled</option>
                </select>
                <InfoTip label="Why Closed isn't in this list" align="end">To close a job use Closed → Close the Job — that&apos;s what posts the equipment and the invoice. A job that isn&apos;t going ahead is set to Cancelled.</InfoTip>
              </span>
            </JField>
            {/* TEST-FLAG-01: a test job never messages anyone. */}
            <label className={cn("flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors",
              d.is_test ? "border-rose-200 bg-rose-50/60" : "border-hairline hover:bg-slate-50")}>
              <input type="checkbox" checked={d.is_test} onChange={(e) => setD({ ...d, is_test: e.target.checked })} className="mt-1 h-4 w-4 accent-rose-600" />
              <span>
                <span className="flex items-center gap-1.5 text-[15px] font-semibold text-slate-800"><FlaskConical className="h-4 w-4 text-rose-600" /> Test Job</span>
                <span className="mt-0.5 flex items-center gap-1.5 text-[14px] leading-snug text-slate-600">
                  Never messages anyone.
                  <InfoTip label="What Test Job does">No customer texts or emails and no work order to the subcontractor — even after messaging goes live. Its visits show a Test tag on the board and the phone. Only a test job can be deleted.</InfoTip>
                </span>
              </span>
            </label>
            {/* JOB-DELETE-01 (Seth 2026-09-30: went looking for delete right
                here, under Edit — it only lived in the page footer, and only
                once the Test flag was already saved). Now the door is where
                he looked: tick Test Job and Delete appears; a real job gets
                Cancelled, never erased. */}
            {d.is_test && canDeleteJob ? (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-rose-200 bg-rose-50/40 p-3">
                <button
                  type="button"
                  disabled={del.isPending}
                  onClick={() => {
                    if (!confirm("Delete this test job? Its unscheduled visits, equipment rows and close-out records go with it. This can't be undone.")) return;
                    del.mutate(job.id, { onSuccess: () => navigate("/jobs") });
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-rose-700 disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" /> {del.isPending ? "Deleting…" : "Delete This Test Job"}
                </button>
                <span className="text-[12px] text-rose-800/80">Everything it created goes too — visits, equipment, close-out records.</span>
                {del.isError && (
                  <span className="w-full text-[12px] font-semibold text-rose-700">
                    {(del.error as Error)?.message?.includes("scheduled") ? "It still has scheduled or worked visits — cancel them on the Board first." : "Couldn't delete — try again."}
                  </span>
                )}
              </div>
            ) : (
              <p className="px-1 text-[12px] leading-snug text-slate-400">
                Built this just to test something? Tick <b>Test Job</b> above and a Delete button appears right here. A real job gets set to <b>Cancelled</b> — never erased.
              </p>
            )}
          </div>
        </div>
      )}

      <div ref={panelTop} className="scroll-mt-28" />

      {/* ── SOLD — what the tech sold, the photos and facts, the load calc, the Equipment Check (JOB-SOLD-01) ── */}
      {view === "sold" && (isNC(job)
        ? <SoldPanel job={job} quote={jobQuote ?? null} onPayments={() => go("closed", "zone-payments")} />
        : <SoldStage job={job} quote={jobQuote ?? null} units={units} closed={closed} onGo={(s, z) => go(s, z)} />)}

      {/* ── WORK ORDER — the plan, in the order it gets built (Seth 2026-09-25):
          the equipment first, then the install details tailored to it, then the
          documents. Sending it to the sub waits for a date (Scheduled). ── */}
      {view === "workorder" && (
        <div className="space-y-5">
          <div id="zone-equipment" className="scroll-mt-28">
            <EquipmentSection job={job} closed={closed}
              checked={isNC(job) ? null : sold.done && job.equip_confirmed_at
                ? { ok: true, text: `Checked against the load calc by ${personOf(job.equip_confirmed_by)}, ${fmtDay(job.equip_confirmed_at)}` }
                : { ok: false, text: sold.stale ? "The units changed since they were checked against the load calc" : "Not checked against the load calc yet" }}
              onCheck={() => go("sold", "zone-equip-check")} />
          </div>
          <div id="zone-install-details" className="scroll-mt-28">
            <InstallDetailsCard key={job.id} job={job} facts={equipFacts} closed={closed} onAddEquipment={() => go("workorder", "zone-equipment-add")} />
          </div>
          <JobDocumentsCard job={job} />
          <InternalNotes
            entityType="job"
            entityId={job.id}
            title="Internal notes — office only"
            placeholder="Working notes for the office: reminders, call summaries, gotchas. NEVER printed or sent — anything the crew or sub must see goes in the Installer Guide (Comments for the Crew)."
          />
        </div>
      )}

      {/* ── SCHEDULED ── */}
      {view === "scheduled" && (
        <div className="space-y-5">
          <section id="zone-visits" className="scroll-mt-28 rounded-xl border border-hairline bg-white p-4">
            <SectionHead icon={CalendarClock} title="Visits" info="Pick the installer and the arrival window — the visit lands on their day on the board's Install view." />
            <div className="space-y-2">
              {(visits ?? []).map((v) => (
                <div key={v.id} className="rounded-lg border border-hairline p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                      <User className="h-4 w-4 text-slate-400" /> {v.visit_role === "install" ? "Install" : (v.visit_role ?? "Visit")}
                    </span>
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">{VISIT_STATUS_LABEL[v.status ?? ""] ?? titleWords(v.status)}</span>
                  </div>
                  {v.summary && <div className="mt-1 text-sm text-slate-600">{v.summary}</div>}
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                    <span className="inline-flex items-center gap-1"><CalendarClock className="h-4 w-4" />{v.scheduled_date ? `${fmtDate(v.scheduled_date)}${v.scheduled_window ? " · " + v.scheduled_window : ""}` : "not scheduled yet"}</span>
                    {v.address && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{v.address}</span>}
                  </div>
                  {!closed && <VisitAssign visit={v} jobId={job.id} />}
                </div>
              ))}
              {(visits ?? []).length === 0 && (
                <div className="rounded-lg border border-dashed border-hairline py-6 text-center">
                  <p className="text-sm text-slate-500">No install visit yet.</p>
                  {!closed && (
                    <Button size="sm" variant="secondary" className="mt-2" disabled={createVisit.isPending}
                      onClick={() => createVisit.mutate({ job_id: job.id, custno: job.custno, locno: job.locno, customer_name: job.customer_name ?? null, address: job.address ?? null, summary: job.title ?? "System install" })}>
                      <Plus className="h-4 w-4" /> {createVisit.isPending ? "Adding…" : "Schedule Install Visit"}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </section>
          {/* SUB-SEND-02: the work order goes to the sub once the install has a date. */}
          <div id="zone-sub" className="scroll-mt-28"><SubcontractorSendCard job={job} visits={visits ?? []} onGo={go} /></div>
        </div>
      )}

      {/* ── PERMIT ── */}
      {view === "permit" && (
        <div className="space-y-5">
          <div id="zone-permit" className="scroll-mt-28"><PermitSection job={job} closed={closed} /></div>
        </div>
      )}

      {/* ── WAREHOUSE — the pull battle station ── */}
      {view === "warehouse" && <WarehouseTab job={job} visits={visits ?? []} />}

      {/* ── INSTALLED — what the crew recorded: units in, old units out, the install record ── */}
      {view === "installed" && (
        <div className="space-y-5">
          <UnitsInstalledCard job={job} units={units} />
          <RemovedEquipmentSection jobId={job.id} />
          {/* INSTALL-CHECK-01 / REFRIG-01 / REBATE-TEST-01: what the crew recorded on the phone. */}
          {!isNC(job) && <div id="zone-install-record" className="scroll-mt-28"><InstallRecordSection job={job} /></div>}
        </div>
      )}

      {/* POST-INSTALL-02 (Seth: "where would I upload warranty certificate,
          permit inspections, what emails have been sent, status of post
          install stuff") — the tracker, the job's file drawer, and every
          email/text queued or sent to this customer. */}
      {view === "post" && (
        <div className="space-y-4">
          {/* POST-READY-01 (Seth: "feels squished"): the readiness board is
              the job's close-out cockpit — it gets the FULL width. */}
          <section className="rounded-xl border border-hairline bg-white p-5">
            <PostInstallCard dispatch={job.source_dispatch} jobId={job.id} custno={job.custno} locno={job.locno} />
          </section>
          <div className="grid gap-4 lg:grid-cols-2">
            {/* FILES-JOB-01: the SAME job files as the Work Order's Documents (this
                was the sale call's file list — a job with no call filed uploads
                under a made-up "job-…" call no other screen could see). */}
            <JobDocumentsCard job={job} />
            <SentComms custno={job.custno} />
          </div>
        </div>
      )}

      {/* ── CLOSED — the money: payments, labor, materials, costing, the invoice, Close the Job ── */}
      {view === "closed" && (
        <div className="space-y-5">
          {/* FIELD-PAY-01: how they're paying (the plan to process) + every payment on the job. */}
          {!isNC(job) && <div id="zone-payments" className="scroll-mt-28"><JobPayPanel job={job} closed={closed} /></div>}
          <LaborSection jobId={job.id} closed={closed} />
          <MaterialsSection jobId={job.id} closed={closed} custno={job.custno} locno={job.locno} sourceDispatch={job.source_dispatch ?? null} />
          <PartsSection jobId={job.id} sellPrice={job.sell_price} />
          <ChangeOrderSection jobId={job.id} closed={closed} />
          <JobSubInvoices jobId={job.id} noSubs={!!manualChecks["no_subs"]}
            onToggleNoSubs={() => onToggleLifecycle("no_subs")} saving={toggleCheck.isPending} />
          <div id="zone-costing" className="scroll-mt-28"><CostingSection jobId={job.id} sellPrice={job.sell_price} saleTech={job.sale_tech} /></div>
          <section className="rounded-xl border border-hairline bg-white p-4">
            <SectionHead icon={Receipt} title="Invoice Message" info="Prints on the customer's install invoice — a thank-you, a care reminder, or a detail about this job." />
            <NotesEditor value={invMsg} onChange={setInvMsg} rows={3} placeholder="Add a message for the customer's invoice…" />
            <div className="mt-2 flex items-center justify-end gap-2">
              {invMsg !== (job.invoice_message ?? "") && (
                <button type="button" onClick={() => setInvMsg(job.invoice_message ?? "")} className="text-xs font-medium text-slate-400 transition-colors hover:text-slate-600">Reset</button>
              )}
              <Button size="sm" disabled={invMsg === (job.invoice_message ?? "") || upd.isPending} onClick={() => upd.mutate({ id: job.id, invoice_message: invMsg.trim() || null })}>
                <Check className="h-3.5 w-3.5" /> {upd.isPending ? "Saving…" : "Save Message"}
              </Button>
            </div>
          </section>
          {isNC(job)
            ? (<>
                <NCBillingSection job={job} closed={closed} />
                <NCCloseOutSection job={job} />
                <OwnershipSection job={job} />
              </>)
            : (<>
                {!closed && <div id="zone-closeout" className="scroll-mt-28"><CloseOutSection job={job} visits={visits ?? []} permitBy={permitBy} piDone={piDone} piTotal={piTotal}
                  custno={job.custno} locno={job.locno} sourceDispatch={job.source_dispatch} /></div>}
                <CustomerInvoicePreview job={job} />
              </>)}
        </div>
      )}

      {/* ── SUMMARY — the whole job, read-only, changes included ── */}
      {view === "summary" && <JobSummaryTab job={job} visits={visits ?? []} costing={costing} />}

      <div className="mt-6 border-t border-hairline pt-4">{footer}</div>
      <style>{`.jinp{border:1px solid var(--hairline,#e2e8f0);border-radius:0.375rem;padding:0.45rem 0.55rem;font-size:0.875rem}.jinp:focus{outline:none;border-color:#6366f1;box-shadow:0 0 0 2px rgb(99 102 241 / 0.25)}`}</style>
    </div>
  );
}

const PAY_PLAN_NAME = { full: "Pay in Full", financing: "Financing", bill_after: "Bill After the Install" } as const;

/** SOLD — the sale at a glance (JOB-NAV-01): when, who, how much, how they're
 *  paying, where it came from. Payments themselves live under Closed. */
function SoldPanel({ job, quote, onPayments }: { job: Job; quote: JobQuote | null; onPayments: () => void }) {
  const memberName = useMemberName();
  return (
    <section className="rounded-xl border border-hairline bg-white p-5">
      <SectionHead icon={HandCoins} title="The Sale" />
      <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
        <div><dt className="text-sm font-semibold text-slate-500">Sold</dt><dd className="mt-0.5 text-lg font-semibold text-slate-900">{fmtDate(job.created_at)}</dd></div>
        <div><dt className="text-sm font-semibold text-slate-500">Sold By</dt><dd className="mt-0.5 text-lg font-semibold text-slate-900">{job.sale_tech ? (memberName(job.sale_tech) ?? job.sale_tech) : "—"}</dd></div>
        <div><dt className="text-sm font-semibold text-slate-500">Contract</dt><dd className="mt-0.5 text-lg font-semibold tabular-nums text-slate-900">{money(job.sell_price)}</dd></div>
        <div><dt className="text-sm font-semibold text-slate-500">How They&apos;re Paying</dt><dd className="mt-0.5 text-[15px] text-slate-800">{quote ? <SalePaymentLine quote={quote} /> : PAY_PLAN_NAME[job.pay_plan ?? "full"]}</dd></div>
      </dl>
      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-hairline pt-3 text-[15px]">
        {quote && (
          <Link to={`/quotes/${quote.id}`} className="inline-flex items-center gap-1.5 font-semibold text-brand-700 hover:underline">
            <FileText className="h-4 w-4" /> The accepted proposal <ArrowRight className="h-4 w-4" />
          </Link>
        )}
        {job.source_dispatch && (
          <Link to={`/dispatch/${job.source_dispatch}`} className="font-semibold text-brand-700 hover:underline">From service call #{job.source_dispatch}</Link>
        )}
        {!isNC(job) && (
          <button type="button" onClick={onPayments} className="inline-flex items-center gap-1.5 font-semibold text-brand-700 hover:underline">
            Payments &amp; financing <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </section>
  );
}

/** INSTALLED — each unit: its serial, the crew's confirmation, the office's. */
function UnitsInstalledCard({ job, units }: { job: Job; units: JobEquipment[] }) {
  const memberName = useMemberName();
  const when = (ts: string | null) => (ts ? new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null);
  return (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <SectionHead icon={Package} title="Units Installed" />
      {job.crew_finished_at && (
        <p className="mb-3 flex flex-wrap items-center gap-x-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span>Install Complete sent {when(job.crew_finished_at)}{job.crew_finished_by ? ` by ${memberName(job.crew_finished_by) ?? job.crew_finished_by.split("@")[0]}` : ""}</span>
          {job.crew_finished_note && <span className="text-emerald-900/80">— {job.crew_finished_note}</span>}
        </p>
      )}
      {units.length === 0 ? (
        <p className="rounded-lg border border-dashed border-hairline py-6 text-center text-sm text-slate-500">No units on this job yet — they're added under Work Order.</p>
      ) : (
        <div className="divide-y divide-hairline">
          {units.map((e) => (
            <div key={e.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2.5 text-sm">
              <span className="min-w-[14rem] flex-1 font-semibold text-slate-800">
                {[e.manufacturer, e.model || e.part_number].filter(Boolean).join(" ") || e.category || "Unit"}
                {e.category && <span className="ml-1.5 font-normal text-slate-500">· {e.category}</span>}
              </span>
              <span className="font-mono text-slate-700">{e.serial ? `S/N ${e.serial}` : <span className="font-sans text-amber-700">No serial yet</span>}</span>
              <span className={e.install_confirmed_at ? "text-emerald-700" : "text-slate-400"}>
                {e.install_confirmed_at ? `✓ Crew confirmed ${when(e.install_confirmed_at)}` : "Crew hasn't confirmed"}
              </span>
              <span className={e.close_confirmed_at ? "text-emerald-700" : "text-slate-400"}>
                {e.close_confirmed_at ? "✓ Office posted" : "Office posts at close"}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


function JField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-[11px] font-medium text-slate-500">{label}</span>
      {children}
    </label>
  );
}

// ── Close-out sections ──────────────────────────────────────────────────────

function Step({ ok, label, clickable, tone }: { ok: boolean; label: string; clickable?: boolean; tone?: "amber" }) {
  return (
    <span className={cn(
      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
      ok ? "bg-emerald-100 text-emerald-700"
        : clickable && tone === "amber" ? "bg-amber-100 text-amber-700 ring-1 ring-inset ring-amber-300 hover:bg-amber-200"
        : clickable ? "bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-200 hover:bg-slate-200"
        : "bg-slate-100 text-slate-400",
    )}>
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <span className="h-3 w-3 rounded-full border border-current opacity-40" />} {label}
    </span>
  );
}


// ── The pricebook picker (Equipment · Materials · Change orders) is the shared
// PricebookPicker (PRICEBOOK-PICK-01): the same search and window everywhere.

function EquipmentSection({ job, closed, checked, onCheck }: {
  job: Job; closed: boolean;
  /** JOB-SOLD-01: the Equipment Check's verdict, shown at the top of the list. */
  checked?: { ok: boolean; text: string } | null;
  onCheck?: () => void;
}) {
  const jobId = job.id;
  const { data: equip } = useJobEquipment(jobId);
  const add = useAddJobEquipment();
  // serial entry (manual + photo-scan + confirm) lives in <SerialField>
  const confirmInstall = useConfirmInstallEquipment();
  const del = useDeleteJobEquipment();
  const [cat, setCat] = useState("");
  const [model, setModel] = useState("");
  const [cost, setCost] = useState("");
  const [itemId, setItemId] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [mfr, setMfr] = useState("");
  const [pn, setPn] = useState("");
  const [price, setPrice] = useState("");
  const upd = useUpdateJobEquipment();
  // Inline edit of one unit's office data (details + our cost + customer price).
  const [edit, setEdit] = useState<null | { id: string; category: string; manufacturer: string; model: string; part_number: string; qty: string; unit_cost: string; unit_price: string }>(null);
  const startEdit = (e: JobEquipment) => setEdit({ id: e.id, category: e.category ?? "", manufacturer: e.manufacturer ?? "", model: e.model ?? "", part_number: e.part_number ?? "", qty: String(e.qty ?? 1), unit_cost: e.unit_cost != null ? String(e.unit_cost) : "", unit_price: e.unit_price != null ? String(e.unit_price) : "" });
  const saveEdit = () => { if (!edit) return; upd.mutate({ id: edit.id, category: edit.category.trim() || null, manufacturer: edit.manufacturer.trim() || null, model: edit.model.trim() || null, part_number: edit.part_number.trim() || null, qty: edit.qty === "" ? 1 : Number(edit.qty), unit_cost: edit.unit_cost === "" ? null : Number(edit.unit_cost), unit_price: edit.unit_price === "" ? null : Number(edit.unit_price) }, { onSuccess: () => setEdit(null) }); };
  const costTotal = (equip ?? []).reduce((s, e) => s + (e.unit_cost ?? 0) * (e.qty || 1), 0);
  const priceTotal = (equip ?? []).reduce((s, e) => s + (e.unit_price ?? 0) * (e.qty || 1), 0);

  // WAREHOUSE-02 (Seth 2026-08-25): this section is WHAT WAS SOLD — the pull
  // itself (checkboxes, mark-all, staging status) lives on the Warehouse tab.
  return (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <SectionHead icon={Package} title="Equipment &amp; Accessories" info="What's going in on this job. It's checked against the load calc on Sold; the warehouse pulls it under Warehouse; the installer guide follows it." />
      {checked && (
        <div className={cn("mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg px-3 py-2 text-sm",
          checked.ok ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-900")}>
          {checked.ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertCircle className="h-4 w-4 shrink-0" />}
          <span className="min-w-0 flex-1">{checked.text}</span>
          {!checked.ok && onCheck && (
            <button type="button" onClick={onCheck} className="inline-flex items-center gap-1 font-semibold text-amber-900 underline-offset-2 hover:underline">
              Equipment Check <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
      <div className="space-y-2.5">
        {(equip ?? []).map((e) => (
          edit?.id === e.id ? (
            <div key={e.id} className="rounded-lg border border-brand-300 bg-white p-3 shadow-sm">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Edit unit</div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <input value={edit.category} onChange={(ev) => setEdit({ ...edit, category: ev.target.value })} placeholder="Type" className={fieldCls} />
                <input value={edit.manufacturer} onChange={(ev) => setEdit({ ...edit, manufacturer: ev.target.value })} placeholder="Manufacturer" className={fieldCls} />
                <input value={edit.model} onChange={(ev) => setEdit({ ...edit, model: ev.target.value })} placeholder="Model" className={fieldCls} />
                <input value={edit.part_number} onChange={(ev) => setEdit({ ...edit, part_number: ev.target.value })} placeholder="Part #" className={fieldCls} />
              </div>
              <div className="mt-2 flex flex-wrap items-end gap-3">
                <label className="block">
                  <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-slate-400">Qty</span>
                  <input type="number" value={edit.qty} onChange={(ev) => setEdit({ ...edit, qty: ev.target.value })} className={cn(fieldCls, "w-16 text-right")} />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-slate-400">Our cost</span>
                  <div className="relative w-28">
                    <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                    <input type="number" value={edit.unit_cost} onChange={(ev) => setEdit({ ...edit, unit_cost: ev.target.value })} placeholder="0" className={cn(fieldCls, "pl-6 text-right tabular-nums")} />
                  </div>
                </label>
                <label className="block">
                  <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-slate-400">Customer price</span>
                  <div className="relative w-28">
                    <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                    <input type="number" value={edit.unit_price} onChange={(ev) => setEdit({ ...edit, unit_price: ev.target.value })} placeholder="0" className={cn(fieldCls, "pl-6 text-right tabular-nums")} />
                  </div>
                </label>
                <div className="ml-auto flex items-center gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setEdit(null)}>Cancel</Button>
                  <Button size="sm" disabled={upd.isPending} onClick={saveEdit}><Check className="h-3.5 w-3.5" /> Save</Button>
                </div>
              </div>
            </div>
          ) : (
            <div key={e.id} className={cn(
              "flex gap-3 rounded-lg border border-hairline p-3.5 transition-colors",
              closed ? "bg-slate-50/60" : "bg-white",
            )}>
              <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-base font-semibold text-slate-800">{[e.manufacturer, e.model || e.part_number].filter(Boolean).join(" ") || "—"}</span>
                    {e.kind === "accessory" && <span className="rounded bg-slate-200/70 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500">accessory</span>}
                    {(e.qty || 1) > 1 && <span className="text-xs text-slate-400">×{e.qty}</span>}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {e.category ? <span className="font-medium text-slate-600">{e.category}</span> : null}
                    {e.model && e.part_number && e.part_number !== e.model ? <span> · #{e.part_number}</span> : null}
                    {/* QUOTE-POLISH-08: the warranty this unit carries — from the
                        proposal line / pricebook model / series rule; a coil takes
                        the condenser's. Posted onto the customer's equipment at close. */}
                    {(e.warranty_parts_years != null || e.warranty_labor_years != null) && (
                      <span className="ml-1.5 inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-px text-[10.5px] font-semibold text-emerald-700 ring-1 ring-emerald-200"
                        title={e.equip_key === "coil" || /coil/i.test(e.category ?? "") ? "Evaporator coil — carries the same warranty as the condenser on this job" : "Carried from the proposal (or the pricebook model). Posts to the customer's equipment when the job closes."}>
                        {e.warranty_parts_years ?? "—"}-yr parts · {e.warranty_labor_years ?? "—"}-yr labor
                      </span>
                    )}
                  </div>
                </div>
                {!closed && (
                  <div className="flex shrink-0 items-center gap-0.5">
                    <button type="button" onClick={() => startEdit(e)} title="Edit unit" className="rounded p-1 text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-600"><Pencil className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => del.mutate(e.id)} title="Remove unit" className="rounded p-1 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                )}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                <span className="text-slate-400">Our cost <span className="font-medium tabular-nums text-slate-600">{e.unit_cost != null ? money(e.unit_cost) : "—"}</span></span>
                <span className="text-slate-400">Customer <span className="font-medium tabular-nums text-slate-600">{e.unit_price != null ? money(e.unit_price) : "—"}</span></span>
                {e.unit_cost != null && e.unit_price != null && <span className={cn("font-medium tabular-nums", e.unit_price - e.unit_cost >= 0 ? "text-emerald-600" : "text-rose-600")}>{e.unit_price - e.unit_cost >= 0 ? "+" : ""}{money(e.unit_price - e.unit_cost)}</span>}
                {(e.unit_cost == null || e.unit_price == null) && !closed && <button type="button" onClick={() => startEdit(e)} className="font-medium text-brand-600 hover:underline">add pricing</button>}
              </div>
              {(e.serial_before || e.model_before || e.manufacturer_before) && (
                <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    Changed on site —{" "}
                    {(e.model_before || e.manufacturer_before) && <>sold <span className="font-mono font-semibold">{[e.manufacturer_before ?? e.manufacturer, e.model_before ?? e.model].filter(Boolean).join(" ")}</span>, installed <span className="font-mono font-semibold">{[e.manufacturer, e.model].filter(Boolean).join(" ")}</span>{e.serial_before ? "; " : ""}</>}
                    {e.serial_before && <>the warehouse logged <span className="font-mono font-semibold">{e.serial_before}</span>, the unit says <span className="font-mono font-semibold">{e.serial ?? "—"}</span></>}
                    . Why: {e.serial_change_reason ?? "not given"}.
                  </span>
                </div>
              )}
              <div className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-2">
                <SerialField key={e.serial ?? ""} jobId={jobId} equip={e} disabled={closed} />
                <div className="ml-auto flex items-center gap-1" title="After install day: the tech confirms the unit went in, then the office verifies it at close-out.">
                  <button type="button" disabled={closed || !e.pulled_at}
                    onClick={() => confirmInstall.mutate({ id: e.id, confirmed: !e.install_confirmed_at })}>
                    <Step ok={!!e.install_confirmed_at} label="Installed" clickable={!closed && !!e.pulled_at} />
                  </button>
                  <ArrowRight className="h-3 w-3 text-slate-300" />
                  <Step ok={!!e.close_confirmed_at} label="Office" />
                </div>
              </div>
              </div>
            </div>
          )
        ))}
        {closed && (equip ?? []).length === 0 && <p className="rounded-lg border border-dashed border-hairline py-6 text-center text-xs text-slate-400">No units on this job.</p>}
        {(equip ?? []).length > 0 && (costTotal > 0 || priceTotal > 0) && (
          <div className="flex flex-wrap items-center justify-end gap-4 border-t border-hairline pt-2 text-xs">
            <span className="text-slate-400">Our cost <span className="font-semibold tabular-nums text-slate-700">{money(costTotal)}</span></span>
            <span className="text-slate-400">Customer <span className="font-semibold tabular-nums text-slate-700">{money(priceTotal)}</span></span>
            <span className="text-slate-400">Margin <span className={cn("font-bold tabular-nums", priceTotal - costTotal >= 0 ? "text-emerald-700" : "text-rose-600")}>{money(priceTotal - costTotal)}</span></span>
          </div>
        )}
      </div>
      {!closed && (
        // GO-FOCUS-01: the add box IS the empty state (was a dead "No units added
        // yet." box with the search buried under the AHRI card). Clicking anywhere
        // in it puts the cursor in the search; "Add the Equipment" lands here.
        <div id="zone-equipment-add" data-go-focus
          onClick={(ev) => {
            if ((ev.target as HTMLElement).closest("input, button, a, select, textarea, label")) return;
            ev.currentTarget.querySelector<HTMLInputElement>("input")?.focus();
          }}
          className={cn("scroll-mt-28 rounded-xl border",
            (equip ?? []).length === 0 ? "cursor-text border-dashed border-brand-300 bg-brand-50/40 p-4" : "mt-3 border-hairline bg-slate-50/40 p-3")}>
          {/* PRICEBOOK-FIRST (JOB-PAGE-02, Seth: "adding equipment needs to be
              nicer"): the default add surface is ONE search box — pick from
              inventory and the cost prefills. The six-field manual grid only
              appears behind "not in the pricebook". */}
          {(equip ?? []).length === 0 ? (
            <div className="mb-3 flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-brand-600 ring-1 ring-inset ring-brand-100">
                <Package className="h-5 w-5" />
              </span>
              <div className="min-w-0 self-center text-[15px] font-semibold text-slate-900">No equipment on this job yet</div>
            </div>
          ) : (
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Add another unit</div>
          )}
          <PricebookPicker pickLabel="Add to Job"
            onPick={(it) => add.mutate({ job_id: jobId, category: it.category ?? null, manufacturer: it.manufacturer ?? null, model: it.model || it.part_number || "", part_number: it.part_number ?? null, unit_cost: itemCostOf(it), unit_price: null, item_id: it.id })}
            onNoMatch={(t) => { setManualOpen(true); setModel(t); }} />
          {!manualOpen ? (
            <button
              type="button"
              onClick={() => setManualOpen(true)}
              className="mt-2 text-xs font-medium text-slate-500 transition-colors hover:text-brand-700"
            >
              Not in the pricebook? Add it by hand →
            </button>
          ) : (
            <>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">By hand</span>
                <button type="button" onClick={() => setManualOpen(false)} className="text-xs font-medium text-slate-400 transition-colors hover:text-slate-600">Hide</button>
              </div>
              <div className="mt-1.5 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <input autoFocus value={cat} onChange={(e) => setCat(e.target.value)} placeholder="Type (AC / Furnace)" className={fieldCls} />
                <input value={mfr} onChange={(e) => setMfr(e.target.value)} placeholder="Manufacturer" className={fieldCls} />
                <input value={model} onChange={(e) => setModel(e.target.value)} placeholder="Model" className={fieldCls} />
                <input value={pn} onChange={(e) => setPn(e.target.value)} placeholder="Part #" className={fieldCls} />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <div className="relative w-28">
                  <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                  <input type="number" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="Our cost" className={cn(fieldCls, "pl-6 text-right tabular-nums")} />
                </div>
                <div className="relative w-32">
                  <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                  <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Customer price" className={cn(fieldCls, "pl-6 text-right tabular-nums")} />
                </div>
                <Button variant="secondary" size="sm" className="ml-auto" disabled={!model.trim() || add.isPending}
                  onClick={() => add.mutate({ job_id: jobId, category: cat.trim() || null, manufacturer: mfr.trim() || null, model: model.trim(), part_number: pn.trim() || null, unit_cost: cost === "" ? null : Number(cost), unit_price: price === "" ? null : Number(price), item_id: itemId }, { onSuccess: () => { setCat(""); setMfr(""); setModel(""); setPn(""); setCost(""); setPrice(""); setItemId(null); } })}>
                  <Plus className="h-3.5 w-3.5" /> Add unit
                </Button>
              </div>
            </>
          )}
        </div>
      )}
      {/* AHRI-01: the AHRI number(s) for this job's matched system — rebates ask
          for it. AHRI-STAY-01 (Seth 2026-09-28: "if i add equipment - that the
          AHRI upload goes away"): it was hidden on a job with no equipment, so it
          came and went as units were added and removed. It stays put now — the
          certificate often arrives before the units are entered. */}
      <AhriCard job={job} closed={closed} />

    </section>
  );
}

function Check2({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <AlertCircle className="h-3.5 w-3.5 text-amber-500" />}
      <span className={ok ? "text-slate-600" : "text-slate-500"}>{label}</span>
    </div>
  );
}

function CloseOutSection({ job, visits, permitBy, piDone, piTotal, custno, locno, sourceDispatch }: {
  job: Job;
  visits: JobVisit[];
  /** PERMIT-01: who pulls it — builder / not needed never blocks the close
   *  (the close_job RPC honors the same answer). */
  permitBy: PermitBy | null;
  piDone?: number;
  piTotal?: number;
  custno?: string | null;
  locno?: string | null;
  sourceDispatch?: string | null;
}) {
  const jobId = job.id;
  const { data: equip } = useJobEquipment(jobId);
  const { data: permits } = useJobPermits(jobId);
  const close = useCloseJob();
  const [err, setErr] = useState<string | null>(null);
  const serials = (equip ?? []).filter((e) => e.serial && e.serial.trim()).length;
  const permitWaived = permitBy === "builder" || permitBy === "none";
  const onFile = (permits ?? []).filter(permitIsOnFile).length;
  const hasPermit = onFile > 0 || permitWaived;
  const ready = serials > 0 && hasPermit;
  // PO-AWARE-01 (Seth 2026-08-25: "will there be a notification... alerting
  // the person closing the job... that there are attached POs?"): the person
  // finalizing the invoice sees, right at the close button, that tagged POs
  // exist — the import itself lives under Materials Used above.
  const { data: matchingPos } = useJobMatchingPos(custno ?? null, locno ?? null, sourceDispatch ?? null, jobId);
  const { data: poParts } = useJobParts(jobId);
  const poCount = (matchingPos ?? []).length;
  // FIELD-PAY-01: what closing does with the money, right at the button.
  const { data: m } = useJobMoney(jobId);
  const { data: jobPays } = useJobPayments(jobId);
  const cashOut = (jobPays ?? [])
    .filter((p) => p.method === "cash" && p.source === "field" && !p.cash_turned_in_at && COUNTED.has(p.status))
    .reduce((s, p) => s + p.amount, 0);
  const usd = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });
  const finWaiting = !!m && m.pay_plan === "financing" && (m.fin_status == null || m.fin_status === "applied");

  return (
    <section className="rounded-xl border border-brand-200 bg-brand-50/30 p-4">
      <SectionHead icon={Lock} title="Close the Job" />
      <p className="mb-3 text-sm leading-relaxed text-slate-600">
        This is the <b>money</b> finish line: it finalizes the invoice at today&apos;s numbers, posts the
        equipment &amp; serials to the customer&apos;s permanent record, and locks the job. Post-Install is a
        separate track — what we <b>send</b> the customer — and it keeps working after the close.
      </p>
      <div className="space-y-1.5 text-sm">
        <Check2 ok={serials > 0} label={`Serial numbers logged (${serials})`} />
        <Check2 ok={hasPermit} label={
          onFile > 0 ? `Permit on file (${onFile})`
          : permitBy === "builder" ? "Permit — the builder pulls it"
          : permitBy === "none" ? "Permit — marked not needed"
          : (permits ?? []).some((p) => p.status === "requested") ? "Permit requested — not pulled yet"
          : "Permit on file"} />
        {/* The crew's record (refrigerant, checklist, rebate test) — warns, never blocks. */}
        <InstallRecordCloseChecks jobId={jobId} />
        {/* CLOSE-INSTALL-DATE: the date the units get on the customer's record. */}
        <InstallDateRow job={job} visits={visits} />
        {piTotal != null && piTotal > 0 && (
          <p className="flex items-center gap-2 pt-0.5 text-xs text-slate-500">
            <ClipboardCheck className="h-3.5 w-3.5 text-slate-400" />
            Post-install items: {piDone ?? 0} of {piTotal} handled
            <InfoTip label="Post-install items and closing">They don&apos;t block the close — rebates often land after.</InfoTip>
          </p>
        )}
        {poCount > 0 && (
          <p className="flex items-start gap-2 pt-0.5 text-[13px] font-medium text-amber-700">
            <Package className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {poCount} purchase order{poCount > 1 ? "s are" : " is"} tagged to this job
              {(poParts ?? []).length > 0 ? ` (${poParts!.length} part lines)` : ""} — make sure the parts are
              imported under <b>Materials Used</b> above before you finalize, so the costs land on this job.
            </span>
          </p>
        )}
      </div>
      {m && m.total > 0.005 && (
        <div className="mt-3 rounded-lg border border-hairline bg-white px-3 py-2.5 text-[13px] text-slate-600">
          <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-slate-500">
            The final invoice
            <InfoTip label="What closing does with the money">Closing puts the deposit and every payment taken for this job on the final invoice, and voids the deposit invoice so nothing is billed twice.</InfoTip>
          </div>
          <div className="flex justify-between"><span>Total</span><span className="tabular-nums text-slate-800">{usd(m.total)}</span></div>
          {m.paid > 0.005 && <div className="flex justify-between"><span>Already paid</span><span className="tabular-nums text-emerald-700">−{usd(m.paid)}</span></div>}
          {m.financed > 0.005 && <div className="flex justify-between"><span>Financing approved — the lender pays</span><span className="tabular-nums">−{usd(m.financed)}</span></div>}
          <div className="mt-1 flex justify-between border-t border-slate-100 pt-1 font-semibold text-slate-800">
            <span>{m.due <= 0.005 ? "Nothing left to collect" : "Left to collect"}</span><span className="tabular-nums">{usd(m.due)}</span>
          </div>
        </div>
      )}
      {cashOut > 0.005 && (
        <p className="mt-2 flex items-start gap-2 text-[13px] font-medium text-amber-700">
          <Banknote className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{usd(cashOut)} cash from the installer isn&apos;t marked received yet (Today → Billing).</span>
        </p>
      )}
      {finWaiting && (
        <p className="mt-2 flex items-start gap-2 text-[13px] font-medium text-amber-700">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Financing isn&apos;t approved yet — the balance stays open on the final invoice until the lender funds it.</span>
        </p>
      )}
      {err && <p className="mt-2 flex items-start gap-1.5 text-[13px] text-rose-600"><AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {err}</p>}
      <Button className="mt-3" disabled={!ready || close.isPending}
        onClick={() => { setErr(null); close.mutate(jobId, { onError: (e) => setErr(e instanceof Error ? e.message : "Close failed.") }); }}>
        <Lock className="h-4 w-4" /> {close.isPending ? "Closing…" : "Close Out Job"}
      </Button>
    </section>
  );
}

/** CLOSE-INSTALL-DATE (Seth 2026-09-25: "the equipment install date should be
 *  the day the crew finishes. But obviously office in closing should be able to
 *  edit / change that if needed"). Shows the date close_job will stamp on the
 *  units — the office's pick, else the day the crew tapped Install Complete, else
 *  the install visit's last day so far, else today — and lets the office change it. */
function InstallDateRow({ job, visits }: { job: Job; visits: JobVisit[] }) {
  const upd = useUpdateJob();
  const today = new Date().toLocaleDateString("en-CA");
  const crewDay = job.crew_finished_at ? new Date(job.crew_finished_at).toLocaleDateString("en-CA", { timeZone: "America/Chicago" }) : null;
  const visitDay = visits.filter((v) => v.visit_role === "install" && v.scheduled_date && v.scheduled_date <= today)
    .map((v) => v.scheduled_date as string).sort().slice(-1)[0] ?? null;
  const auto = crewDay ?? visitDay ?? today;
  const shown = job.install_date ?? auto;
  const why = job.install_date ? "set by the office"
    : crewDay ? "the day the crew sent Install Complete"
    : visitDay ? "the install visit's day — the crew hasn't sent Install Complete"
    : "today — no install day on file yet";
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(shown);
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 pt-0.5">
      <CalendarClock className="h-4 w-4 shrink-0 text-brand-500" />
      <span className="text-slate-700">Install date on the customer&apos;s equipment record: <b className="text-slate-900">{fmtDate(shown)}</b></span>
      {!editing && <span className="text-slate-500">— {why}</span>}
      {!editing ? (
        <button type="button" onClick={() => { setVal(shown); setEditing(true); }} className="font-semibold text-brand-700 hover:underline">Change</button>
      ) : (
        <span className="inline-flex flex-wrap items-center gap-2">
          <input type="date" value={val} max={today} onChange={(e) => setVal(e.target.value)} className="jinp" />
          <Button size="sm" disabled={!val || upd.isPending} onClick={() => upd.mutate({ id: job.id, install_date: val }, { onSuccess: () => setEditing(false) })}>
            <Check className="h-3.5 w-3.5" /> Save
          </Button>
          {job.install_date && (
            <button type="button" disabled={upd.isPending} onClick={() => upd.mutate({ id: job.id, install_date: null }, { onSuccess: () => setEditing(false) })}
              className="font-semibold text-slate-600 hover:underline">Use the crew&apos;s day</button>
          )}
          <button type="button" onClick={() => setEditing(false)} className="font-medium text-slate-500 hover:underline">Cancel</button>
        </span>
      )}
    </div>
  );
}

// ── Import materials from a Purchase Order (search any PO, pull its lines in) ─
// Covers the "someone attached it wrong / I need to find it" case: search every
// PO (Pegasus-native + ESC) by #, vendor, or memo and import its lines as
// materials. Resolves each line to a pricebook item by part # so close-out can
// deduct on-hand.
function ImportFromPoModal({ jobId, onClose, initialTerm }: { jobId: string; onClose: () => void; initialTerm?: string }) {
  const [term, setTerm] = useState(initialTerm ?? "");
  const [picked, setPicked] = useState<{ source: "pegasus" | "esc"; id: string; label: string } | null>(null);
  const [importing, setImporting] = useState(false);
  const add = useAddJobMaterial();
  const { data: nativePos } = useNativePos();
  const { data: escPos } = useEscPoList(term);
  const { data: nativeLines } = useNativePoLines(picked?.source === "pegasus" ? picked.id : null);
  const { data: escLines } = useEscPoLines(picked?.source === "esc" ? picked.id : null);

  const t = term.trim().toLowerCase();
  const nativeMatches = (nativePos ?? []).filter((p) =>
    !t || `${p.po_number ?? ""} ${p.memo ?? ""} ${p.vendor_name ?? ""} ${p.dispatch_no ?? ""}`.toLowerCase().includes(t),
  ).slice(0, 10);
  const escMatches = (escPos ?? []).slice(0, 10);

  const lines = picked?.source === "pegasus"
    ? (nativeLines ?? []).map((l) => ({ part_number: l.part_number, description: l.description, qty: Number(l.qty || 0), unit_cost: Number(l.unit_cost || 0) }))
    : (escLines ?? []).map((l) => ({ part_number: l.part, description: l.description, qty: Number(l.quan || 0), unit_cost: Number(l.price || 0) }));

  const doImport = async () => {
    setImporting(true);
    try {
      const parts = lines.map((l) => l.part_number).filter(Boolean) as string[];
      const idByPart = new Map<string, string>();
      if (parts.length) {
        const { data } = await supabase.from("item").select("id, part_number").in("part_number", parts);
        for (const it of (data ?? []) as { id: string; part_number: string }[]) idByPart.set(it.part_number, it.id);
      }
      for (const l of lines) {
        if (!l.part_number && !l.description) continue;
        await add.mutateAsync({
          job_id: jobId, description: l.part_number || l.description || "Part",
          qty: l.qty || 1, unit_cost: l.unit_cost || null, print: true,
          item_id: l.part_number ? idByPart.get(l.part_number) ?? null : null,
        });
      }
      onClose();
    } finally { setImporting(false); }
  };

  return (
    <Modal open onClose={onClose} title="Import from a PO" subtitle="Search any purchase order and pull its lines in as materials" size="md">
      {!picked ? (
        <div className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input autoFocus value={term} onChange={(e) => setTerm(e.target.value)} placeholder="PO #, vendor, memo, dispatch…" className="jinp w-full !pl-9" />
          </div>
          <div className="max-h-80 space-y-1.5 overflow-auto">
            {nativeMatches.map((p) => (
              <button key={`p-${p.id}`} type="button" onClick={() => setPicked({ source: "pegasus", id: p.id, label: p.po_number ?? "PO" })}
                className="flex w-full items-center justify-between gap-2 rounded-md border border-hairline px-2.5 py-1.5 text-left text-xs hover:bg-slate-50">
                <span className="min-w-0 truncate"><span className="font-medium text-slate-700">PO {p.po_number ?? "—"}</span> <span className="text-slate-400">{p.vendor_name ?? p.vendor_code ?? ""}{p.memo ? " · " + p.memo : ""}</span></span>
                <span className="flex shrink-0 items-center gap-1.5"><span className="rounded bg-brand-50 px-1 py-0.5 text-[10px] font-semibold text-brand-600">Pegasus</span><span className="tabular-nums text-slate-500">{money(p.total)}</span></span>
              </button>
            ))}
            {escMatches.map((p) => (
              <button key={`e-${p.po}`} type="button" onClick={() => setPicked({ source: "esc", id: p.po, label: p.po })}
                className="flex w-full items-center justify-between gap-2 rounded-md border border-hairline px-2.5 py-1.5 text-left text-xs hover:bg-slate-50">
                <span className="min-w-0 truncate"><span className="font-medium text-slate-700">PO {p.po}</span> <span className="text-slate-400">{p.vendor_name ?? p.vendor_code ?? ""}{p.memo ? " · " + p.memo : ""}</span></span>
                <span className="shrink-0 rounded bg-slate-100 px-1 py-0.5 text-[10px] font-semibold text-slate-500">ESC</span>
              </button>
            ))}
            {t.length >= 1 && nativeMatches.length === 0 && escMatches.length === 0 && (
              <p className="py-6 text-center text-xs text-slate-400">No POs match “{term.trim()}”.</p>
            )}
            {t.length === 0 && nativeMatches.length === 0 && (
              <p className="py-6 text-center text-xs text-slate-400">Start typing a PO #, vendor, or memo.</p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <button type="button" onClick={() => setPicked(null)} className="text-xs text-brand-600 hover:underline">← back to search</button>
          <div className="text-sm font-medium text-slate-700">PO {picked.label} — {lines.length} line{lines.length === 1 ? "" : "s"}</div>
          <div className="max-h-72 overflow-auto rounded-md border border-hairline">
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-400"><th className="px-2 py-1.5">Part</th><th className="px-2 py-1.5 text-right">Qty</th><th className="px-2 py-1.5 text-right">Cost</th></tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-t border-hairline"><td className="px-2 py-1.5 text-slate-700">{l.part_number || l.description || "—"}</td><td className="px-2 py-1.5 text-right tabular-nums">{l.qty}</td><td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{money(l.unit_cost)}</td></tr>
                ))}
                {lines.length === 0 && <tr><td colSpan={3} className="px-2 py-4 text-center text-slate-400">No lines on this PO.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-hairline pt-3">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" disabled={lines.length === 0 || importing} onClick={doImport}><Plus className="h-3.5 w-3.5" /> {importing ? "Importing…" : `Import ${lines.length} item${lines.length === 1 ? "" : "s"}`}</Button>
          </div>
        </div>
      )}
      <style>{`.jinp{border:1px solid var(--hairline,#e2e8f0);border-radius:0.375rem;padding:0.45rem 0.55rem;font-size:0.875rem}.jinp:focus{outline:none;border-color:#6366f1;box-shadow:0 0 0 2px rgb(99 102 241 / 0.25)}`}</style>
    </Modal>
  );
}

// ── Materials (installer-logged, print-aware) ───────────────────────────────
// Materials/accessories used on the job. Each has an eye toggle that controls
// whether it prints on the customer invoice (ESC's per-line Print checkbox);
// inventory-only items like refrigerant stay for cost/inventory but don't print.
function MaterialsSection({ jobId, closed, custno, locno, sourceDispatch }: { jobId: string; closed: boolean; custno: string | null; locno: string | null; sourceDispatch: string | null }) {
  const { data: mats } = useJobMaterials(jobId);
  const add = useAddJobMaterial();
  const upd = useUpdateJobMaterial();
  const del = useDeleteJobMaterial();
  const [desc, setDesc] = useState("");
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("");
  const [cost, setCost] = useState("");
  const [print, setPrint] = useState(true);
  const [poOpen, setPoOpen] = useState(false);
  const [poTerm, setPoTerm] = useState("");
  const { data: jobParts } = useJobParts(jobId);
  const { data: matchingPos } = useJobMatchingPos(custno, locno, sourceDispatch, jobId);
  const hidden = (mats ?? []).filter((m) => !m.print).length;
  // Pull the line items from POs attached to this job straight onto the invoice as materials.
  const importFromPO = () => {
    for (const p of jobParts ?? []) {
      add.mutate({ job_id: jobId, description: p.part_number || p.description || "Part", qty: p.qty || 1, unit_cost: p.unit_cost ?? null, print: true });
    }
  };

  return (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <SectionHead icon={Droplets} title="Materials Used" note={hidden > 0 ? `${hidden} hidden` : undefined} />
      <div className="space-y-1.5">
        {(mats ?? []).map((m) => (
          <div key={m.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-hairline bg-slate-50/60 px-3 py-2 text-sm">
            <span className="flex min-w-0 items-center gap-1.5">
              <button type="button" disabled={closed}
                title={m.print ? "Prints on the customer invoice — click to hide" : "Hidden from the customer invoice — click to show"}
                onClick={() => upd.mutate({ id: m.id, print: !m.print })}
                className={cn("shrink-0 transition-colors", m.print ? "text-emerald-600 hover:text-emerald-700" : "text-slate-300 hover:text-slate-500")}>
                {m.print ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
              <span className="min-w-0">
                <span className={cn("block truncate", m.print ? "text-slate-700" : "text-slate-400 line-through decoration-slate-300")}>{m.description}</span>
                {/* FIELD-CREWS-01: what the crew changed on the iPad, and why. */}
                {/* MATERIAL-REQ-01: a line the crew asked the warehouse for, and where it is. */}
                {m.source === "request" && (
                  <span className="block text-[11px] leading-snug text-sky-700">
                    Requested by the crew · {lineStatusText(m)}{m.item_id ? "" : " · not in the catalog"}{m.po_id ? " · its cost is on the PO" : ""}
                  </span>
                )}
                {(m.source === "field" || m.source === "addon" || (m.qty_actual != null && m.change_reason)) && (
                  <span className="block text-[11px] leading-snug text-amber-700">
                    {m.source === "field" ? "Added on site" : m.source === "addon" ? "Add-on sold on site" : `Used ${m.qty_actual} (planned ${m.qty})`}
                    {m.change_reason && m.source !== "addon" ? ` · ${m.change_reason}` : ""}
                    {m.changed_by ? ` — ${m.changed_by.split("@")[0]}` : ""}
                  </span>
                )}
              </span>
              {!m.print && <span className="shrink-0 rounded bg-slate-200/70 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500">inventory only</span>}
            </span>
            <span className="flex shrink-0 items-center gap-2 text-slate-500">
              <span className="flex items-center gap-1">
                <span className="text-[10px] uppercase text-slate-400">used</span>
                <input key={`${m.id}:${m.qty_actual ?? ""}`} defaultValue={m.qty_actual ?? ""} disabled={closed} placeholder={String(m.qty)}
                  onBlur={(ev) => { const v = ev.target.value.trim(); const n = v === "" ? null : Number(v); if (n !== (m.qty_actual ?? null)) upd.mutate({ id: m.id, qty_actual: n }); }}
                  className={cn(fieldCls, "w-14 text-right")} aria-label="actual used" />
                {m.unit && <span className="text-[10px] text-slate-400">{m.unit}</span>}
              </span>
              {m.unit_cost != null && <span className="w-14 text-right tabular-nums text-slate-400">{money(materialCost(m))}</span>}
              {!closed && <button type="button" onClick={() => del.mutate(m.id)} title="Remove material" className="shrink-0 rounded p-1 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>}
            </span>
          </div>
        ))}
        {(mats ?? []).length === 0 && <p className="rounded-lg border border-dashed border-hairline py-6 text-center text-xs text-slate-400">No materials logged yet.</p>}
      </div>
      {!closed && (matchingPos?.length ?? 0) > 0 && (
        <button type="button" onClick={() => { setPoTerm(matchingPos![0].dispatch || matchingPos![0].po_number || ""); setPoOpen(true); }}
          className="mt-3 flex w-full items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left text-xs text-amber-800 transition-colors hover:bg-amber-100">
          <Package className="h-4 w-4 shrink-0 text-amber-600" />
          <span className="min-w-0 flex-1">
            <span className="font-semibold">{matchingPos!.length} purchase order{matchingPos!.length > 1 ? "s" : ""} match this job</span>
            {matchingPos![0].matchedBy === "dispatch" && matchingPos![0].dispatch ? ` — dispatch #${matchingPos![0].dispatch}` : " — same address"}. Review &amp; import the parts.
          </span>
          <ArrowRight className="h-3.5 w-3.5 shrink-0" />
        </button>
      )}
      {!closed && (
        <div className="mt-3 space-y-2 rounded-lg border border-hairline bg-slate-50/40 p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Add material</div>
          <div className="flex flex-wrap items-center gap-2">
            <PricebookPicker className="min-w-[18rem] flex-1" pickLabel="Add Material"
              onPick={(it) => add.mutate({ job_id: jobId, description: it.description || it.part_number || it.model || "Material", qty: 1, unit: it.units, unit_cost: itemCostOf(it), print: true, item_id: it.id })} />
            {(jobParts?.length ?? 0) > 0 && (
              <Button variant="secondary" size="sm" disabled={add.isPending} onClick={importFromPO} title="Pull the parts from POs attached to this job onto the invoice">
                <Package className="h-3.5 w-3.5" /> Import from PO ({jobParts!.length})
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => setPoOpen(true)} title="Search any PO by number, vendor, or memo and import its lines">
              <Search className="h-3.5 w-3.5" /> Find a PO…
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Add a manual item" className={cn(fieldCls, "min-w-[9rem] flex-1")} />
            <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="qty" className={cn(fieldCls, "w-14 text-right")} />
            <input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="unit" className={cn(fieldCls, "w-16")} />
            <div className="relative w-20">
              <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
              <input type="number" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="cost" className={cn(fieldCls, "pl-6 text-right tabular-nums")} />
            </div>
            <button type="button" onClick={() => setPrint(!print)}
              title={print ? "Will print on the customer invoice" : "Inventory only — won't print"}
              className={cn("inline-flex items-center gap-1 rounded-md border px-2 py-1.5 text-[11px] font-medium transition-colors", print ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-500")}>
              {print ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />} {print ? "Prints" : "Hidden"}
            </button>
            <Button variant="secondary" size="sm" disabled={!desc.trim() || add.isPending}
              onClick={() => add.mutate({ job_id: jobId, description: desc.trim(), qty: qty === "" ? 1 : Number(qty), unit: unit.trim() || null, unit_cost: cost === "" ? null : Number(cost), print }, { onSuccess: () => { setDesc(""); setQty(""); setUnit(""); setCost(""); setPrint(true); } })}>
              <Plus className="h-3.5 w-3.5" /> Add
            </Button>
          </div>
        </div>
      )}
      <p className="mt-2 text-[11px] leading-relaxed text-slate-400">
        Installers log what they actually used. Items marked <EyeOff className="inline h-3 w-3" /> (e.g. refrigerant) stay on the job for cost &amp; inventory but never print on the customer invoice{hidden > 0 ? ` — ${hidden} hidden now` : ""}. Stock is only deducted when you close out the job.
      </p>
      {poOpen && <ImportFromPoModal jobId={jobId} initialTerm={poTerm} onClose={() => { setPoOpen(false); setPoTerm(""); }} />}
    </section>
  );
}

// ── Customer invoice (Sedgwick-styled, print-aware preview) ─────────────────
/** Warranty end = install date + term (10yr equipment, 5yr controls). */
function addYears(iso: string, n: number): string {
  const d = new Date(iso.length <= 10 ? iso + "T00:00:00" : iso);
  d.setFullYear(d.getFullYear() + n);
  return d.toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" });
}
function CustomerInvoicePreview({ job }: { job: Job }) {
  const memberName = useMemberName();
  const { data: equip } = useJobEquipment(job.id);
  const { data: permits } = useJobPermits(job.id);
  const { data: mats } = useJobMaterials(job.id);
  const { data: labor } = useJobLabor(job.id);
  const { data: inv } = useJobInvoice(job.id);
  const { data: changeOrders } = useChangeOrders(job.id);
  const closed = job.status === "closed";
  const installISO = job.closed_at ?? new Date().toISOString();
  const tech = (labor ?? [])[0]?.crew ?? null;
  // "Installer" on an install invoice = the lead installer assigned to the install visit
  // (the same person the board shows), falling back to whoever logged labor hours.
  const { data: invVisits } = useJobVisits(job.id);
  const leadInstaller = (invVisits ?? []).find((v) => v.visit_role === "install" && v.assigned_empno)?.assigned_empno ?? null;
  const installer = memberName(leadInstaller) ?? tech ?? null;
  const printedMats = (mats ?? []).filter((m) => m.print);
  const hiddenMats = (mats ?? []).filter((m) => !m.print);
  const approvedCOs = (changeOrders ?? []).filter((c) => c.status === "approved");

  // Invoice-only customization overlay — show/hide any line + one-off custom lines.
  const { data: lineEdits } = useInvoiceLineEdits(job.id);
  const setHidden = useSetInvoiceLineHidden();
  const addCustom = useAddCustomInvoiceLine();
  const delEdit = useDeleteInvoiceLineEdit();
  const [customizing, setCustomizing] = useState(false);
  const [clName, setClName] = useState("");
  const [clAmt, setClAmt] = useState("");
  const hiddenKeys = new Set((lineEdits ?? []).filter((e) => e.line_key && e.hidden).map((e) => e.line_key as string));
  const customLines = (lineEdits ?? []).filter((e) => e.is_custom);
  const hiddenEdits = (lineEdits ?? []).filter((e) => e.line_key && e.hidden);
  const visEquip = (equip ?? []).filter((e) => !hiddenKeys.has(`equip:${e.id}`));
  const visMats = printedMats.filter((m) => !hiddenKeys.has(`material:${m.id}`));
  const visCOs = approvedCOs.filter((c) => !hiddenKeys.has(`co:${c.id}`));
  const coAddl = visCOs.reduce((s, c) => s + Number(c.amount || 0), 0);
  const customAddl = customLines.reduce((s, c) => s + Number(c.amount || 0), 0);
  const base = job.sell_price ?? 0;
  const total = closed && inv?.total != null ? Number(inv.total) : base + coAddl + customAddl;
  // MONEY-LINK-01 (Seth 2026-08-25: "I adjusted the furnace... nothing updates
  // in the contract, margins, pdf"): the contract price is editable RIGHT HERE,
  // and when equipment lines carry proposal prices that add up differently,
  // one click makes the line total the contract — contract, margin, and the
  // printed invoice all read the same number.
  const updJob = useUpdateJob();
  const pricedEquip = visEquip.filter((e) => e.unit_price != null);
  const eqLineTotal = pricedEquip.reduce((s, e) => s + Number(e.unit_price ?? 0) * (e.qty || 1), 0);
  const lineMismatch = !closed && pricedEquip.length > 0 && Math.abs(eqLineTotal - base) > 0.005;
  const equipCat = (c: string | null) => (c && c.trim() ? c : "Equipment");
  const isControl = (c: string | null) => /(stat|thermostat|control)/i.test(c ?? "");
  const hideBtn = (lineKey: string) =>
    customizing && !closed ? (
      <button type="button" onClick={() => setHidden.mutate({ job_id: job.id, line_key: lineKey, hidden: true })} title="Hide this line from the customer invoice" className="ml-1.5 rounded p-0.5 align-middle text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-500">
        <EyeOff className="inline h-3 w-3" />
      </button>
    ) : null;
  const updateCustom = useUpdateCustomInvoiceLine();
  const editByKey = new Map((lineEdits ?? []).filter((e) => e.line_key).map((e) => [e.line_key as string, e]));
  const labelFor = (key: string, derived: string) => editByKey.get(key)?.label ?? derived;
  // Rename a line's printed text (reuses the hidden-upsert, which preserves the flag).
  const renameCell = (key: string, derived: string) =>
    customizing && !closed ? (
      <input key={`${key}:${labelFor(key, derived)}`} defaultValue={labelFor(key, derived)}
        onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== labelFor(key, derived)) setHidden.mutate({ job_id: job.id, line_key: key, hidden: false, label: v }); }}
        className="w-full max-w-[18rem] rounded-md border border-hairline bg-white px-2 py-1 text-sm" />
    ) : (
      <>{labelFor(key, derived)}</>
    );

  // Unified, reorderable "Equipment & materials" summary (equipment + materials +
  // custom lines as one list). Order persists via each line's `sort`; a reorder
  // writes an explicit sort to every line so the order is stable.
  // PAY-02: a FINALIZED invoice with an open balance collects right from the workspace.
  const canCollect = useCan("payments.collect");
  // FIELD-PAY-01: the preview shows the payments the printed invoice will —
  // before close the deposit + money held for the job, after close the final
  // invoice's own (docPaymentLines: one rule for every renderer).
  const finalInv = closed && inv && (inv as { type?: string | null }).type === "install" ? inv : null;
  const { data: payLedger } = useCustomerPayments(job.custno);
  const { data: jobInvs } = useJobInvoices(closed ? null : job.id);
  const depositIds = new Set((jobInvs ?? []).filter((i) => (i as { type?: string | null }).type === "deposit" && (i as { status?: string | null }).status !== "void").map((i) => i.id));
  const invBalance = finalInv?.total != null ? Math.max(0, Number(finalInv.total) - (invoicePaidMap(payLedger).get(finalInv.id) ?? 0)) : 0;
  const payLines = finalInv
    ? docPaymentLines(payLedger, (a) => a.invoice_id === finalInv.id)
    : closed
      ? []
      : docPaymentLines(
          payLedger,
          (a) => (a.job_id === job.id && !a.invoice_id) || (!!a.invoice_id && depositIds.has(a.invoice_id)),
          (a) => !!a.invoice_id && depositIds.has(a.invoice_id),
        );
  const previewBalance = finalInv ? invBalance : Math.max(0, total - payLines.reduce((s, l) => s + l.amount, 0));
  const { data: jm } = useJobMoney(job.id);
  const finPending = previewBalance > 0.005 && jm && jm.pay_plan === "financing" && jm.fin_status === "approved" && Number(jm.fin_amount ?? 0) > 0.005
    ? { amount: Number(jm.fin_amount), name: jm.fin_plan_name }
    : null;
  const usd = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });
  const [showCollect, setShowCollect] = useState(false);

  const reorder = useReorderInvoiceLines();
  type SumItem = { uid: string; kind: "equip" | "mat" | "custom"; line_key: string | null; derived: string; custom: (typeof customLines)[number] | null };
  const summaryRaw: SumItem[] = [
    ...visEquip.map((e) => ({ uid: `equip:${e.id}`, kind: "equip" as const, line_key: `equip:${e.id}`, derived: e.model ? `${equipCat(e.category)} — ${e.model}` : equipCat(e.category), custom: null })),
    ...visMats.map((m) => ({ uid: `material:${m.id}`, kind: "mat" as const, line_key: `material:${m.id}`, derived: m.description ?? "Material", custom: null })),
    ...customLines.map((c) => ({ uid: `custom:${c.id}`, kind: "custom" as const, line_key: null, derived: c.label ?? "", custom: c })),
  ];
  const sortOf = (it: SumItem) => (it.kind === "custom" ? it.custom?.sort : editByKey.get(it.line_key as string)?.sort);
  const summaryItems = summaryRaw
    .map((it, i) => ({ it, i, s: sortOf(it) }))
    .sort((a, b) => (a.s ?? a.i) - (b.s ?? b.i) || a.i - b.i)
    .map((x) => x.it);
  const moveLine = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= summaryItems.length) return;
    const arr = summaryItems.slice();
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    reorder.mutate({
      job_id: job.id,
      order: arr.map((it, i) => ({
        editId: it.kind === "custom" ? it.custom?.id : editByKey.get(it.line_key as string)?.id,
        line_key: it.kind === "custom" ? null : it.line_key,
        sort: i,
      })),
    });
  };

  return (
    <section>
      <h3 className="mb-3 flex items-center justify-between gap-1.5 text-[15px] font-bold tracking-tight text-slate-900">
        <span className="flex items-center gap-2"><Receipt className="h-[18px] w-[18px]" /> Customer Invoice</span>
        <span className="flex items-center gap-2">
          <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", closed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700")}>{closed ? "Finalized" : "Preview — draft"}</span>
          {!closed && (
            <button type="button" onClick={() => setCustomizing((v) => !v)}
              className={cn("inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold normal-case transition-colors", customizing ? "border-brand-300 bg-brand-50 text-brand-700" : "border-hairline text-slate-600 hover:bg-slate-50")}>
              <Pencil className="h-3.5 w-3.5" /> {customizing ? "Done Customizing" : "Customize"}
            </button>
          )}
          <button type="button" onClick={() => window.open(`/jobs/${job.id}/invoice`, "_blank")}
            className="inline-flex items-center gap-1.5 rounded-lg border border-hairline px-3 py-1.5 text-xs font-bold normal-case text-slate-600 transition-colors hover:bg-slate-50">
            <Printer className="h-3.5 w-3.5" /> Print / PDF
          </button>
          {canCollect && finalInv && invBalance > 0.005 && (
            <button type="button" onClick={() => setShowCollect(true)}
              title={`Open balance ${money(invBalance)}`}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-1.5 text-xs font-bold normal-case text-white shadow-e1 transition-colors hover:bg-brand-800">
              <HandCoins className="h-3.5 w-3.5" /> Collect Payment
            </button>
          )}
        </span>
      </h3>
      {showCollect && finalInv && (
        <CollectPaymentModal
          custno={job.custno}
          customerName={job.customer_name ?? null}
          invoices={[{
            id: finalInv.id,
            number: (finalInv as { invoice_no?: string | null }).invoice_no || `SH-${job.source_dispatch ?? job.id.slice(0, 6).toUpperCase()}`,
            date: null,
            label: "Install Invoice",
            total: Number(finalInv.total ?? 0),
            balance: invBalance,
          }]}
          initialInvoiceId={finalInv.id}
          onClose={() => setShowCollect(false)}
        />
      )}

      <div className="overflow-hidden rounded-xl border border-hairline bg-white text-[14px] leading-relaxed text-slate-700 shadow-sm">
        <div className="flex items-start justify-between gap-3 border-b border-hairline px-6 py-5">
          <div>
            <div className="text-xl font-bold tracking-tight text-slate-900">Sedgwick Heating &amp; Air Conditioning</div>
            <div className="text-[13px] text-slate-400">Pay your bill online · sedgwickheating.com/pay</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold uppercase tracking-wide text-slate-800">Invoice</div>
            <div className="text-[13px] text-slate-500">{new Date(installISO).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 border-b border-hairline px-6 py-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Bill to</div>
            <div className="text-[15px] font-semibold text-slate-800">{job.customer_name ?? job.custno}</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Job</div>
            <div className="text-[15px] text-slate-700">{job.title ?? "System installation"}</div>
            {job.address && <div className="text-[13px] text-slate-400">{job.address}</div>}
          </div>
          <div className="col-span-2 mt-1 grid grid-cols-3 gap-2 border-t border-hairline pt-3 text-[13px]">
            <div><span className="text-[11px] uppercase tracking-wide text-slate-400">Comfort Advisor</span><div className="font-medium text-slate-700">{memberName(job.sale_tech) ?? "—"}</div></div>
            <div><span className="text-[11px] uppercase tracking-wide text-slate-400">Installer</span><div className="font-medium text-slate-700">{installer ?? "—"}</div></div>
            <div><span className="text-[11px] uppercase tracking-wide text-slate-400">Dispatch</span><div className="font-medium text-slate-700">{job.source_dispatch ?? "—"}</div></div>
          </div>
        </div>
        {visEquip.length > 0 && (
          <div className="border-b border-hairline px-6 py-4">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Equipment installed</div>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
                  <th className="pb-1.5 pr-2 font-medium">Manufacturer</th><th className="pb-1.5 pr-2 font-medium">Model</th>
                  <th className="pb-1.5 pr-2 font-medium">Serial #</th><th className="pb-1.5 pr-2 font-medium">Type</th>
                  <th className="pb-1.5 font-medium">Warranty</th>
                </tr>
              </thead>
              <tbody>
                {visEquip.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100">
                    <td className="py-1.5 pr-2">{manufacturerLabel(e)}</td>
                    <td className="py-1.5 pr-2 font-medium text-slate-700">{e.model ?? "—"}</td>
                    <td className="py-1.5 pr-2 tabular-nums text-slate-500">{e.serial ?? "—"}</td>
                    <td className="py-1.5 pr-2 text-slate-500">{equipCat(e.category)}</td>
                    <td className="py-1.5 text-slate-500">{addYears(installISO, isControl(e.category) ? 5 : 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-6 py-4">
          <table className="w-full">
            <tbody>
              <tr className="border-b border-slate-100">
                <td className="py-2 text-[15px] font-semibold text-slate-800">Total Contract Price</td>
                <td className="py-2 text-right text-[15px] font-semibold tabular-nums text-slate-800">
                  {customizing && !closed ? (
                    <span className="relative inline-block">
                      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                      <input key={`cp:${base}`} type="number" defaultValue={base || ""}
                        onBlur={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); if (v !== job.sell_price) updJob.mutate({ id: job.id, sell_price: v }); }}
                        title="The contract price from the proposal — edit it and the invoice, margin, and PDF all follow"
                        className="w-32 rounded-lg border border-brand-300 bg-white py-1.5 pl-6 pr-2 text-right text-[15px] font-semibold tabular-nums focus:border-brand-500 focus:outline-none" />
                    </span>
                  ) : money(base)}
                </td>
              </tr>
              {lineMismatch && (
                <tr>
                  <td colSpan={2} className="pb-1 pt-1.5">
                    <span className="flex flex-wrap items-center gap-2 rounded-lg bg-sky-50 px-3 py-2 text-[13px] text-sky-800">
                      Priced equipment lines add up to <b className="tabular-nums">{money(eqLineTotal)}</b> — contract says <b className="tabular-nums">{money(base)}</b>.
                      <button type="button" disabled={updJob.isPending}
                        onClick={() => updJob.mutate({ id: job.id, sell_price: eqLineTotal })}
                        className="rounded-md bg-sky-700 px-2.5 py-1 text-xs font-bold text-white transition-colors hover:bg-sky-800">
                        Use Line Total
                      </button>
                    </span>
                  </td>
                </tr>
              )}
              {(permits ?? []).filter(permitIsOnFile).length > 0 && (
                <>
                  <tr><td className="pt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400" colSpan={2}>Permits</td></tr>
                  {(permits ?? []).filter(permitIsOnFile).map((p) => (
                    <tr key={p.id}><td className="py-1 text-slate-600" colSpan={2}>{permitLabel(p.trade)}{p.permit_no ? ` #: ${p.permit_no}` : ""}</td></tr>
                  ))}
                </>
              )}
              {(visEquip.length > 0 || visMats.length > 0 || customLines.length > 0) && (
                <>
                  <tr><td className="pt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400" colSpan={2}>Equipment &amp; materials</td></tr>
                  {summaryItems.map((it, idx) => {
                    const rowLabel = (
                      <span className="inline-flex items-center gap-1 align-middle">
                        {customizing && !closed && (
                          <span className="mr-0.5 inline-flex flex-col leading-none">
                            <button type="button" disabled={idx === 0} onClick={() => moveLine(idx, -1)} title="Move up" className="rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-25"><ChevronUp className="h-3.5 w-3.5" /></button>
                            <button type="button" disabled={idx === summaryItems.length - 1} onClick={() => moveLine(idx, 1)} title="Move down" className="rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-25"><ChevronDown className="h-3.5 w-3.5" /></button>
                          </span>
                        )}
                        <span>
                          {it.kind === "custom"
                            ? (customizing && !closed
                                ? <input key={`cl:${it.custom!.id}:${it.custom!.label ?? ""}`} defaultValue={it.custom!.label ?? ""} onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== (it.custom!.label ?? "")) updateCustom.mutate({ id: it.custom!.id, job_id: job.id, label: v, amount: it.custom!.amount }); }} className="w-full max-w-[18rem] rounded-md border border-hairline bg-white px-2 py-1 text-sm" />
                                : it.custom!.label)
                            : renameCell(it.line_key as string, it.derived)}
                          {it.kind === "custom"
                            ? (customizing && !closed && <button type="button" onClick={() => delEdit.mutate({ id: it.custom!.id, job_id: job.id })} title="Remove custom line" className="ml-1.5 rounded p-0.5 align-middle text-slate-300 hover:bg-rose-50 hover:text-rose-500"><X className="inline h-3 w-3" /></button>)
                            : hideBtn(it.line_key as string)}
                        </span>
                      </span>
                    );
                    return (
                      <tr key={it.uid}>
                        {it.kind === "custom" ? (
                          <>
                            <td className="py-1 text-slate-600">{rowLabel}</td>
                            {customizing && !closed
                              ? <td className="py-1 text-right"><input key={`cla:${it.custom!.id}:${it.custom!.amount ?? ""}`} type="number" defaultValue={it.custom!.amount ?? ""} onBlur={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); if (v !== it.custom!.amount) updateCustom.mutate({ id: it.custom!.id, job_id: job.id, label: it.custom!.label ?? "", amount: v }); }} className="w-28 rounded-md border border-hairline bg-white px-2 py-1 text-right text-sm tabular-nums" /></td>
                              : (it.custom!.amount != null ? <td className="py-1 text-right tabular-nums text-slate-700">{money(it.custom!.amount)}</td> : <td />)}
                          </>
                        ) : (
                          <td className="py-1 text-slate-600" colSpan={2}>{rowLabel}</td>
                        )}
                      </tr>
                    );
                  })}
                </>
              )}
              {visCOs.length > 0 && (
                <>
                  <tr><td className="pt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400" colSpan={2}>Change orders</td></tr>
                  {visCOs.map((c) => (
                    <tr key={c.id}>
                      <td className="py-1 text-slate-600">{renameCell(`co:${c.id}`, c.description ?? "Change order")}{hideBtn(`co:${c.id}`)}</td>
                      <td className="py-1 text-right tabular-nums text-slate-700">{money(c.amount)}</td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
          <div className="mt-4 flex items-center justify-between border-t-2 border-slate-200 pt-3">
            <span className="text-lg font-semibold text-slate-700">Total</span>
            <span className="text-xl font-bold tabular-nums text-slate-900">{payLines.length > 0 || finPending ? usd(total) : money(total)}</span>
          </div>
          {payLines.map((l) => (
            <div key={l.key} className="mt-1.5 flex items-start justify-between gap-3 text-[13px] text-slate-600">
              <span>{l.label}{l.method ? ` — ${l.method}` : ""}{l.reference ? ` · ${l.reference}` : ""}</span>
              <span className="shrink-0 tabular-nums text-emerald-700">−{usd(l.amount)}</span>
            </div>
          ))}
          {(payLines.length > 0 || finPending) && (
            <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2">
              <span className="text-[15px] font-semibold text-slate-700">{previewBalance <= 0.005 ? "Paid in Full" : "Balance Due"}</span>
              <span className="text-lg font-bold tabular-nums text-slate-900">{usd(previewBalance)}</span>
            </div>
          )}
          {finPending && (
            <div className="mt-1 flex items-start justify-between gap-3 text-[13px] text-slate-600">
              <span>Financing approved{finPending.name ? ` — ${finPending.name}` : ""} · the lender pays this part</span>
              <span className="shrink-0 tabular-nums">{usd(finPending.amount)}</span>
            </div>
          )}
          {job.invoice_message && (
            <div className="mt-3 rounded-lg bg-slate-50 px-4 py-3 text-[13px] italic leading-relaxed text-slate-600">
              {job.invoice_message}
            </div>
          )}
        </div>
        <div className="border-t border-hairline bg-slate-50 px-6 py-2.5 text-center text-[11px] text-slate-400">
          See our full terms and conditions at <a href="https://sedgwickheating.com/terms" target="_blank" rel="noreferrer" className="font-medium text-slate-500 hover:underline">sedgwickheating.com/terms</a>
        </div>
      </div>
      {customizing && !closed && (
        <div className="mt-3 space-y-2.5 rounded-xl border border-brand-200 bg-brand-50/40 p-3.5">
          <p className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-700">
            Customize the Customer Copy
            <InfoTip label="How customizing works">Click the eye beside a line to hide it from the customer copy, click a name or amount to edit it, or add a one-off line below. These changes never change the job itself.</InfoTip>
          </p>
          <form onSubmit={(e) => { e.preventDefault(); if (clName.trim()) addCustom.mutate({ job_id: job.id, label: clName, amount: clAmt === "" ? null : Number(clAmt) }, { onSuccess: () => { setClName(""); setClAmt(""); } }); }} className="flex flex-wrap items-center gap-2">
            <input value={clName} onChange={(e) => setClName(e.target.value)} placeholder="Custom line (e.g. Old equipment removal)" className="h-10 min-w-[12rem] flex-1 rounded-lg border border-hairline bg-white px-3 text-sm focus:border-brand-400 focus:outline-none" />
            <div className="relative w-32"><span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span><input type="number" value={clAmt} onChange={(e) => setClAmt(e.target.value)} placeholder="amount" className="h-10 w-full rounded-lg border border-hairline bg-white pl-6 pr-2.5 text-right text-sm tabular-nums focus:border-brand-400 focus:outline-none" /></div>
            <Button variant="secondary" disabled={!clName.trim() || addCustom.isPending}><Plus className="h-4 w-4" /> Add Line</Button>
          </form>
          {hiddenEdits.length > 0 && (
            <div className="border-t border-brand-200/60 pt-1.5">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Hidden from this invoice</div>
              <div className="flex flex-wrap gap-1.5">
                {hiddenEdits.map((h) => {
                  const k = h.line_key as string;
                  const lbl = k.startsWith("equip:") ? ((equip ?? []).find((x) => `equip:${x.id}` === k)?.model ?? "Equipment")
                    : k.startsWith("material:") ? ((mats ?? []).find((x) => `material:${x.id}` === k)?.description ?? "Material")
                    : k.startsWith("co:") ? (approvedCOs.find((x) => `co:${x.id}` === k)?.description ?? "Change order") : k;
                  return (
                    <button key={h.id} type="button" onClick={() => setHidden.mutate({ job_id: job.id, line_key: k, hidden: false })} title="Show this line again" className="inline-flex items-center gap-1 rounded-full border border-hairline bg-white px-2 py-0.5 text-[11px] text-slate-500 hover:bg-slate-50">
                      <Eye className="h-3 w-3" /> {lbl}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      {!customizing && (hiddenMats.length > 0 || hiddenEdits.length > 0) && (
        <p className="mt-1.5 flex items-start gap-1 text-[11px] text-slate-400">
          <EyeOff className="mt-0.5 h-3 w-3 shrink-0" /> <span>Hidden from this invoice: {[
            ...hiddenEdits.map((h) => {
              const k = h.line_key as string;
              return k.startsWith("equip:") ? ((equip ?? []).find((x) => `equip:${x.id}` === k)?.model ?? "Equipment")
                : k.startsWith("material:") ? ((mats ?? []).find((x) => `material:${x.id}` === k)?.description ?? "Material")
                : k.startsWith("co:") ? (approvedCOs.find((x) => `co:${x.id}` === k)?.description ?? "Change order") : k;
            }),
            ...hiddenMats.map((m) => m.description),
          ].filter(Boolean).join(", ")} — kept on the job for cost &amp; inventory{closed ? "; left off the finalized invoice" : ""}.</span>
        </p>
      )}
      <p className="mt-1 text-[11px] text-slate-400">{closed ? `Finalized ${fmtDate(job.closed_at)}.` : "Live preview — finalizes when you close out the job."} Equipment shown by model; costs &amp; part numbers stay internal.</p>
    </section>
  );
}

function PartsSection({ jobId, sellPrice }: { jobId: string; sellPrice: number | null }) {
  const { data: parts } = useJobParts(jobId);
  if (!parts || parts.length === 0) return null;
  const cost = parts.reduce((s, p) => s + p.qty * p.unit_cost, 0);
  return (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <SectionHead icon={Package} title="Parts Ordered for This Job" />
      <div className="overflow-hidden rounded-lg border border-hairline">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-400">
              <th className="px-2 py-1.5">Part</th><th className="px-2 py-1.5 text-right">Qty</th>
              <th className="px-2 py-1.5 text-right">Cost</th><th className="px-2 py-1.5 text-right">PO</th>
            </tr>
          </thead>
          <tbody>
            {parts.map((p, i) => (
              <tr key={`${p.po_id}-${i}`} className="border-t border-hairline">
                <td className="px-2 py-1.5 text-slate-700">{p.part_number ?? p.description ?? "—"}{p.received && <span className="ml-1 text-[10px] font-semibold uppercase text-emerald-600">received</span>}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{p.qty}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">{money(p.unit_cost)}</td>
                <td className="px-2 py-1.5 text-right text-slate-400">{p.po_number}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-1.5 text-right text-[11px] text-slate-400">
        Parts cost <strong className="text-slate-600">{money(cost)}</strong>
        {sellPrice != null && <> · sell {money(sellPrice)}</>} <span className="text-slate-300">(feeds Job costing below)</span>
      </p>
    </section>
  );
}

function LaborSection({ jobId, closed }: { jobId: string; closed: boolean }) {
  const { data: labor } = useJobLabor(jobId);
  const add = useAddJobLabor();
  const del = useDeleteJobLabor();
  const { data: techs } = useAllActiveTechs(); // installers/crews across departments
  const { data: rates } = useTechRates(); // empno → hourly cost (auto-fills the rate)
  const [empno, setEmpno] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [hours, setHours] = useState("");
  const [rate, setRate] = useState("");
  const totalH = (labor ?? []).reduce((s, l) => s + Number(l.hours || 0), 0);
  const techName = (e: string) => {
    const t = (techs ?? []).find((x) => x.empno === e);
    return t ? (t.firstname ? `${t.firstname} ${t.lastname ?? ""}`.trim() : t.empname ?? t.empno) : null;
  };
  return (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <SectionHead icon={Clock} title="Labor — Daily Hours" note={totalH > 0 ? `${totalH}h total` : undefined} info="Hours worked on this job by day — they feed the job cost." />
      <div className="space-y-1.5">
        {(labor ?? []).map((l) => (
          <div key={l.id} className="flex items-center justify-between gap-2 rounded-lg border border-hairline bg-slate-50/60 px-3 py-2 text-sm">
            <span className="text-slate-700">
              <span className="font-semibold text-slate-800">{l.crew ?? "—"}</span> <span className="text-xs text-slate-400">· {l.work_date ? fmtDate(l.work_date) : "—"} · {l.hours}h{l.rate != null ? ` @ ${money(l.rate)}/h` : ""}</span>
              {l.source === "mobile" && <span className="ml-1.5 rounded bg-brand-50 px-1 py-0.5 text-[10px] font-semibold uppercase text-brand-600">mobile</span>}
            </span>
            {!closed && <button type="button" onClick={() => del.mutate(l.id)} title="Remove entry" className="shrink-0 rounded p-1 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>}
          </div>
        ))}
        {(labor ?? []).length === 0 && <p className="rounded-lg border border-dashed border-hairline py-6 text-center text-xs text-slate-400">No hours logged yet.</p>}
      </div>
      {!closed && (
        <div className="mt-3 rounded-lg border border-hairline bg-slate-50/40 p-3">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Log hours</div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={empno} onChange={(e) => { const v = e.target.value; setEmpno(v); const r = rates?.get(v); if (r != null) setRate(String(r)); }} className={cn(fieldCls, "w-40")}>
              <option value="">Crew member…</option>
              {(techs ?? []).map((t) => <option key={t.empno} value={t.empno}>{techName(t.empno)}</option>)}
            </select>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={cn(fieldCls, "w-auto")} />
            <input type="number" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="hrs" className={cn(fieldCls, "w-16 text-right")} />
            <div className="relative w-24">
              <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
              <input type="number" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="/hr" className={cn(fieldCls, "pl-6 text-right tabular-nums")} title="Auto-filled from the team member's pay rate — override if needed" />
            </div>
            <Button variant="secondary" size="sm" className="ml-auto" disabled={!empno || hours === "" || add.isPending}
              onClick={() => add.mutate({ job_id: jobId, tech_empno: empno || null, crew: techName(empno), work_date: date || null, hours: Number(hours), rate: rate === "" ? null : Number(rate) }, { onSuccess: () => { setEmpno(""); setHours(""); setRate(""); } })}>
              <Plus className="h-3.5 w-3.5" /> Log
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function ChangeOrderSection({ jobId, closed }: { jobId: string; closed: boolean }) {
  const { data: cos } = useChangeOrders(jobId);
  const add = useAddChangeOrder();
  const setStatus = useUpdateChangeOrderStatus();
  const del = useDeleteChangeOrder();
  const [desc, setDesc] = useState("");
  const [amt, setAmt] = useState("");
  const [sig, setSig] = useState<string | null>(null);
  if (closed && (cos ?? []).length === 0) return null;
  return (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <SectionHead icon={FilePlus2} title="Change Orders" info="Approved change orders change the contract amount. Add-ons sold on site come in approved — the customer signed for them on the iPad." />
      <div className="space-y-1.5">
        {(cos ?? []).map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-2 rounded-lg border border-hairline bg-slate-50/60 px-3 py-2 text-sm">
            <span className="min-w-0">
              <span className="block truncate text-slate-700">{c.description ?? "—"} <strong className="tabular-nums text-slate-800">{money(c.amount)}</strong></span>
              {/* FIELD-CREWS-01: an add-on sold on site — the customer signed on the iPad. */}
              {c.source === "field" && (
                <span className="flex flex-wrap items-center gap-x-2 text-[11px] text-slate-500">
                  <span>Signed on the iPad{c.signed_name ? ` by ${c.signed_name}` : ""}{c.signed_at ? ` · ${new Date(c.signed_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}</span>
                  {c.sold_by_name && <span className="font-medium text-slate-600">Sold by {c.sold_by_name}</span>}
                  {c.signature_url && <button type="button" onClick={() => setSig(c.signature_url ?? null)} className="font-medium text-brand-600 hover:underline">signature</button>}
                </span>
              )}
            </span>
            <span className="flex shrink-0 items-center gap-2">
              <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", c.status === "approved" ? "bg-emerald-100 text-emerald-700" : c.status === "rejected" ? "bg-rose-100 text-rose-600" : "bg-amber-100 text-amber-700")}>{c.status}</span>
              {!closed && c.status === "pending" && <button type="button" onClick={() => setStatus.mutate({ id: c.id, status: "approved" })} className="text-[11px] font-medium text-emerald-600 hover:underline">approve</button>}
              {!closed && <button type="button" onClick={() => del.mutate(c.id)} title="Remove change order" className="shrink-0 rounded p-1 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>}
            </span>
          </div>
        ))}
        {(cos ?? []).length === 0 && <p className="rounded-lg border border-dashed border-hairline py-6 text-center text-xs text-slate-400">No change orders.</p>}
      </div>
      {!closed && (
        <div className="mt-3 space-y-2 rounded-lg border border-hairline bg-slate-50/40 p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Add a change order</div>
          <PricebookPicker pickLabel="Use This Item" browseMulti={false}
            onPick={(it) => { setDesc(it.description || it.part_number || it.model || ""); const a = it.base_price ?? itemCostOf(it); setAmt(a != null ? String(a) : ""); }} />
          <div className="flex flex-wrap items-center gap-2">
            <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Or type a change (e.g. add humidifier)" className={cn(fieldCls, "min-w-[10rem] flex-1")} />
            <div className="relative w-28">
              <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
              <input type="number" value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="amount" className={cn(fieldCls, "pl-6 text-right tabular-nums")} />
            </div>
            <Button variant="secondary" size="sm" disabled={!desc.trim() || amt === "" || add.isPending}
              onClick={() => add.mutate({ job_id: jobId, description: desc.trim(), amount: Number(amt) }, { onSuccess: () => { setDesc(""); setAmt(""); } })}>
              <Plus className="h-3.5 w-3.5" /> Add
            </Button>
          </div>
        </div>
      )}
      {sig && (
        <Modal open onClose={() => setSig(null)} title="Customer signature">
          <img src={sig} alt="Customer signature" className="w-full rounded-lg border border-hairline bg-white" />
        </Modal>
      )}
    </section>
  );
}

/** FIELD-CREWS-01: the old equipment the crew took out (entered on the iPad). */
function RemovedEquipmentSection({ jobId }: { jobId: string }) {
  const { data: removed } = useRemovedEquipment(jobId);
  const list = removed ?? [];
  // The old units' data-tag photos live in the job's files.
  const { data: jobFiles } = useJobFiles(jobId);
  const tagFiles = (jobFiles ?? []).filter((f) => list.some((r) => r.tag_file_id === f.id));
  const { data: tagUrls } = useSignedUrls(tagFiles);
  const tagUrl = (id: string | null | undefined) => {
    const f = tagFiles.find((x) => x.id === id);
    return f ? tagUrls?.get(f.storage_path) ?? null : null;
  };
  // REFRIG-01: what came out of each old unit (the EPA recovery record).
  const { data: refLogs } = useJobRefrigerant(jobId);
  const { data: tanks } = useRefrigerantTanks();
  const recovered = (id: string) => (refLogs ?? []).find((l) => l.kind === "recover" && l.removed_equipment_id === id) ?? null;
  if (list.length === 0) return null;
  return (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <SectionHead icon={Package} title="Coming Out" info="The old equipment the crew removed, logged on the iPad." />
      <div className="space-y-1.5">
        {list.map((r) => (
          <div key={r.id} className="flex flex-wrap items-baseline justify-between gap-2 rounded-lg border border-hairline bg-slate-50/60 px-3 py-2 text-sm">
            <span className="min-w-0">
              <span className="font-semibold text-slate-800">{r.category || "Old unit"}</span>
              {(r.brand || r.model) && <span className="text-slate-600"> · {[r.brand, r.model].filter(Boolean).join(" ")}</span>}
              {r.serial && <span className="font-mono text-xs text-slate-500"> · S/N {r.serial}</span>}
              {r.year_made && <span className="text-xs text-slate-500"> · made {r.year_made}</span>}
              {(() => {
                const rec = recovered(r.id);
                if (!rec) return null;
                const tank = rec.tank_id ? tanks?.find((t) => t.id === rec.tank_id)?.label ?? "a recovery tank" : null;
                return (
                  <span className="block text-xs text-slate-600">
                    {rec.refrigerant} · {rec.empty ? "it was empty (leaked out)" : `${ozText(rec.oz)} recovered${tank ? ` into ${tank}` : ""}`}
                  </span>
                );
              })()}
              {r.notes && <span className="block text-xs text-slate-500">“{r.notes}”</span>}
            </span>
            <span className="flex shrink-0 items-center gap-2 text-xs text-slate-500">
              {tagUrl(r.tag_file_id) && <a href={tagUrl(r.tag_file_id)!} target="_blank" rel="noreferrer" className="font-medium text-brand-600 hover:underline">Tag photo</a>}
              {DISPOSITION_LABEL[r.disposition]}{r.created_by ? ` · ${r.created_by.split("@")[0]}` : ""}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function CostLine({ label, value }: { label: string; value: number }) {
  return <div className="flex justify-between"><span className="text-slate-500">{label}</span><span className="tabular-nums text-slate-600">{money(value)}</span></div>;
}

function CostingSection({ jobId, sellPrice, saleTech }: { jobId: string; sellPrice: number | null; saleTech: string | null }) {
  const { data: parts } = useJobParts(jobId);
  const { data: equip } = useJobEquipment(jobId);
  const { data: labor } = useJobLabor(jobId);
  const { data: permits } = useJobPermits(jobId);
  const { data: cos } = useChangeOrders(jobId);
  const { data: jobMats } = useJobMaterials(jobId);
  // MONEY-LINK-01: quoted matches the customer invoice EXACTLY — contract +
  // approved change orders + custom invoice lines. One number, everywhere.
  const { data: lineEdits } = useInvoiceLineEdits(jobId);
  const customAddl = (lineEdits ?? []).filter((e) => e.is_custom).reduce((s, c) => s + Number(c.amount || 0), 0);
  const materials = (parts ?? []).reduce((s, p) => s + p.qty * p.unit_cost, 0);
  const fieldMat = (jobMats ?? []).reduce((s, m) => s + materialCost(m), 0);
  const equipCost = (equip ?? []).reduce((s, e) => s + (e.unit_cost ?? 0) * (e.qty || 1), 0);
  const laborCost = (labor ?? []).reduce((s, l) => s + Number(l.hours || 0) * Number(l.rate || 0), 0);
  const permitCost = (permits ?? []).reduce((s, p) => s + (p.by_builder || p.status === "cancelled" ? 0 : (p.fee ?? 0)), 0);
  // REFRIG-01: refrigerant the crew weighed in (Log the Charge), at the cost per lb when it was logged.
  const { data: refLogs } = useJobRefrigerant(jobId);
  const { data: refItems } = useRefrigerantItems();
  const refCost = refrigerantCost(refLogs ?? [], refItems);
  const actual = materials + fieldMat + equipCost + laborCost + permitCost + refCost;
  const approvedCO = (cos ?? []).filter((c) => c.status === "approved").reduce((s, c) => s + Number(c.amount || 0), 0);
  const quoted = (sellPrice ?? 0) + approvedCO + customAddl;
  const variance = quoted - actual;
  return (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <SectionHead icon={Calculator} title="Job Costing" info="What the customer pays against what the job cost us, by cost type." />
      <div className="mb-3 rounded-lg bg-slate-50 p-3.5">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Margin</div>
        <div className={cn("text-3xl font-bold leading-tight tabular-nums", variance >= 0 ? "text-emerald-700" : "text-rose-600")}>{money(variance)}</div>
        <div className="mt-1 flex flex-wrap gap-x-5 gap-y-0.5 text-sm text-slate-500">
          <span>Invoice total <span className="font-semibold tabular-nums text-slate-800">{money(quoted)}</span></span>
          <span>Our cost <span className="font-semibold tabular-nums text-slate-800">{money(actual)}</span></span>
        </div>
        {(approvedCO > 0 || customAddl > 0) && (
          <div className="mt-0.5 text-xs text-slate-400">
            Invoice total = contract {money(sellPrice ?? 0)}
            {approvedCO > 0 ? <> + {money(approvedCO)} change orders</> : null}
            {customAddl > 0 ? <> + {money(customAddl)} custom invoice lines</> : null}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
        <CostLine label="Materials (POs)" value={materials} />
        <CostLine label="Materials (field)" value={fieldMat} />
        <CostLine label="Equipment" value={equipCost} />
        <CostLine label="Labor" value={laborCost} />
        <CostLine label="Permits" value={permitCost} />
        {refCost > 0.005 && <CostLine label="Refrigerant" value={refCost} />}
      </div>
      <SalesCreditsEditor jobId={jobId} saleTech={saleTech} />
    </section>
  );
}

// New-construction → homeowner handoff. The property keeps the home's equipment + warranty;
// the builder keeps the construction billing. One action ends the builder tenure, starts the homeowner's.
function OwnershipSection({ job }: { job: Job }) {
  const { data } = useJobProperty(job.id, job.property_id);
  const handoff = useNewHomeownerHandoff();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const ownership = data?.ownership ?? [];
  const hasHomeowner = ownership.some((o) => o.role === "owner" && !o.end_date);

  const submit = () => {
    if (!name.trim()) return;
    handoff.mutate(
      { job: { id: job.id, custno: job.custno, customer_name: job.customer_name, site_address: job.site_address, site_city: job.site_city, address: job.address, property_id: job.property_id }, homeowner_name: name },
      { onSuccess: () => { setAdding(false); setName(""); } },
    );
  };

  return (
    <section>
      <h3 className="mb-2 flex flex-wrap items-center gap-1.5 text-[15px] font-bold tracking-tight text-slate-900">
        <Home className="h-3.5 w-3.5" /> Ownership
        <InfoTip label="About ownership">When the home sells, hand it to the homeowner — the equipment, serials and warranty travel with the house; the builder keeps the construction billing. Nothing is deleted.</InfoTip>
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200">⚑ Sedgwick new build</span>
      </h3>

      {ownership.length === 0 ? (
        <div className="rounded-lg border border-hairline px-2.5 py-2 text-xs">
          <span className="font-medium text-slate-700">{job.customer_name ?? "Builder"}</span> <span className="text-slate-400">· builder (new construction) · current</span>
        </div>
      ) : (
        <div className="space-y-1">
          {ownership.slice().reverse().map((o) => (
            <div key={o.id} className={cn("flex items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5 text-xs", o.end_date ? "border-hairline" : "border-emerald-200 bg-emerald-50/40")}>
              <span><span className={cn("font-medium", o.end_date ? "text-slate-500" : "text-slate-800")}>{o.party_name}</span> <span className="text-slate-400">· {o.role}</span></span>
              <span className="tabular-nums text-slate-400">{o.start_date ? fmtDate(o.start_date) : "—"}{o.end_date ? ` – ${fmtDate(o.end_date)}` : " · current"}</span>
            </div>
          ))}
        </div>
      )}

      {!hasHomeowner && (
        adding ? (
          <div className="mt-2 flex items-center gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="homeowner name" className="jinp flex-1" />
            <Button size="sm" disabled={!name.trim() || handoff.isPending} onClick={submit}><Check className="h-3.5 w-3.5" /> {handoff.isPending ? "…" : "Confirm"}</Button>
            <Button variant="secondary" size="sm" onClick={() => { setAdding(false); setName(""); }}>Cancel</Button>
          </div>
        ) : (
          <button type="button" onClick={() => setAdding(true)} className="mt-2 inline-flex items-center gap-1 rounded-md border border-hairline px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50">
            <ArrowRight className="h-3.5 w-3.5" /> New homeowner here
          </button>
        )
      )}
    </section>
  );
}

